// src/money.ts
import { Decimal as DecimalJs } from "decimal.js";
var Dec = DecimalJs.clone({ precision: 40, rounding: DecimalJs.ROUND_HALF_EVEN });
var dec = (v) => v instanceof Dec ? v : new Dec(v);
var SYMBOLS = { USD: "$", AUD: "A$", PKR: "PKR ", EUR: "\u20AC", GBP: "\xA3" };
function formatMoney(amount, currency, opts = {}) {
  const d = dec(amount);
  const decimals = opts.decimals ?? (d.abs().gte(1e3) ? 0 : 2);
  const abs = d.abs().toFixed(decimals);
  const [int, frac] = abs.split(".");
  const grouped = (int ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body2 = (SYMBOLS[currency] ?? `${currency} `) + grouped + (frac ? `.${frac}` : "");
  if (d.isNeg() && !d.isZero()) return `\u2212${body2}`;
  if (opts.signed && d.gt(0)) return `+${body2}`;
  return body2;
}
function formatPct(value, decimals = 2) {
  return `${dec(value).toFixed(decimals)} %`;
}
function fxRate(from, to, unitsPerUsd) {
  if (from === to) return new Dec(1);
  const a = from === "USD" ? new Dec(1) : unitsPerUsd[from] !== void 0 ? dec(unitsPerUsd[from]) : null;
  const b = to === "USD" ? new Dec(1) : unitsPerUsd[to] !== void 0 ? dec(unitsPerUsd[to]) : null;
  if (!a || !b || a.isZero()) return null;
  return b.div(a);
}
function convert(amount, from, to, unitsPerUsd) {
  const r = fxRate(from, to, unitsPerUsd);
  return r ? dec(amount).mul(r) : null;
}

// src/risk.ts
var DEFAULT_RISK_POLICY = {
  autoMaxRiskPct: "1.0",
  hardMaxRiskPct: "2.0",
  maxOpenRiskPct: "4.0",
  maxOpenTrades: 5,
  dailyLossLimitPct: "3.0",
  minRewardRisk: "1.5",
  requireStopLoss: true,
  requireLinkedAnalysis: false,
  allowUnassignedSymbols: false,
  outsideSessionAction: "ESCALATE"
};
var NAMES = {
  SL_REQUIRED: "Stop loss set",
  RISK_PER_TRADE: "Risk per trade",
  OPEN_RISK: "Total open risk",
  OPEN_TRADES: "Open trades",
  DAILY_LOSS_LOCK: "Daily loss lock",
  REWARD_RISK: "Reward : risk",
  SYMBOL_ASSIGNED: "Symbol assigned",
  SESSION_OPEN: "Market session",
  LINKED_ANALYSIS: "Linked analysis",
  FX_RATE_FRESH: "FX rate"
};
var pct = (d) => `${d.toFixed(2)} %`;
function evaluateRisk(input) {
  const checks = [];
  const add = (code, result, message, value, limit) => checks.push({ code, name: NAMES[code], result, message, ...value !== void 0 ? { value } : {}, ...limit !== void 0 ? { limit } : {} });
  const p = input.policy;
  const entry = dec(input.entry);
  const size = dec(input.size);
  const equity = dec(input.account.equity);
  const stop = input.stopLoss === null || input.stopLoss === void 0 || input.stopLoss === "" ? null : dec(input.stopLoss);
  const long = input.direction === "LONG";
  let rate = null;
  const sameCcy = input.instrument.quoteCurrency === input.account.currency;
  if (sameCcy) {
    rate = new Dec(1);
    add("FX_RATE_FRESH", "NOT_APPLICABLE", `Not needed: ${input.instrument.quoteCurrency} instrument on a ${input.account.currency} account.`);
  } else if (!input.fx) {
    add("FX_RATE_FRESH", "ESCALATE", `No ${input.instrument.quoteCurrency}\u2192${input.account.currency} rate is available, so risk can't be measured.`);
  } else {
    rate = dec(input.fx.rate);
    if (input.fx.ageDays > input.context.fxMaxAgeDays) {
      add("FX_RATE_FRESH", "ESCALATE", `The newest ${input.instrument.quoteCurrency}\u2192${input.account.currency} rate is ${input.fx.ageDays} days old (max ${input.context.fxMaxAgeDays}).`, input.fx.rateDate);
    } else {
      add("FX_RATE_FRESH", "PASS", `Rate ${rate.toSignificantDigits(8).toString()} from ${input.fx.rateDate}.`, input.fx.rateDate);
    }
  }
  let stopValid = false;
  if (!stop) {
    add("SL_REQUIRED", p.requireStopLoss ? "REJECT" : "ESCALATE", p.requireStopLoss ? "No stop loss set. Every plan on this account needs one." : "No stop loss set, so risk can't be measured.");
  } else if (long && stop.gte(entry) || !long && stop.lte(entry)) {
    add("SL_REQUIRED", "REJECT", `Stop ${stop.toString()} is on the wrong side of entry ${entry.toString()} for a ${long ? "long" : "short"}.`);
  } else {
    stopValid = true;
    add("SL_REQUIRED", "PASS", `Stop at ${stop.toString()}, ${entry.minus(stop).abs().toString()} from entry.`);
  }
  let riskAmount = null;
  let riskPct = null;
  if (stopValid && stop && rate && equity.gt(0)) {
    riskAmount = entry.minus(stop).abs().mul(dec(input.instrument.pointValue)).mul(size).mul(rate);
    riskPct = riskAmount.div(equity).mul(100);
  }
  if (riskPct) {
    const auto = dec(p.autoMaxRiskPct);
    const hard = dec(p.hardMaxRiskPct);
    if (riskPct.lte(auto)) add("RISK_PER_TRADE", "PASS", `${pct(riskPct)} is within the ${pct(auto)} auto-approve limit.`, pct(riskPct), pct(auto));
    else if (riskPct.lte(hard)) add("RISK_PER_TRADE", "ESCALATE", `${pct(riskPct)} is above ${pct(auto)} auto-approve, under the ${pct(hard)} hard max.`, pct(riskPct), pct(hard));
    else add("RISK_PER_TRADE", "REJECT", `${pct(riskPct)} is above the ${pct(hard)} hard max.`, pct(riskPct), pct(hard));
    const openTotal = dec(input.account.openRiskAmount).plus(riskAmount).div(equity).mul(100);
    const maxOpen = dec(p.maxOpenRiskPct);
    add("OPEN_RISK", openTotal.lte(maxOpen) ? "PASS" : "ESCALATE", `${pct(openTotal)} of the ${pct(maxOpen)} account limit, including open trades.`, pct(openTotal), pct(maxOpen));
  } else {
    add("RISK_PER_TRADE", "ESCALATE", "Risk can't be measured until the stop and FX rate are valid.");
    add("OPEN_RISK", "NOT_APPLICABLE", "Needs a measurable risk amount.");
  }
  const trades = input.account.openTradeCount + 1;
  add("OPEN_TRADES", trades <= p.maxOpenTrades ? "PASS" : "ESCALATE", `${trades} of ${p.maxOpenTrades} open trades with this one.`, String(trades), String(p.maxOpenTrades));
  const lossLimit = dec(p.dailyLossLimitPct);
  const lossUsed = equity.gt(0) ? Dec.max(0, dec(input.account.realisedPnlToday).neg()).div(equity).mul(100) : new Dec(0);
  if (input.account.dailyLossLocked || lossUsed.gte(lossLimit)) {
    add("DAILY_LOSS_LOCK", "REJECT", `Daily loss limit reached (${pct(lossUsed)} of ${pct(lossLimit)}). New plans are blocked until the next trading day.`, pct(lossUsed), pct(lossLimit));
  } else {
    add("DAILY_LOSS_LOCK", "PASS", `${pct(lossUsed)} of the ${pct(lossLimit)} daily loss limit used.`, pct(lossUsed), pct(lossLimit));
  }
  const rewardRisk = [];
  const firstTp = input.takeProfits[0];
  if (!firstTp) {
    add("REWARD_RISK", "ESCALATE", "No target set, so reward : risk is unknown.");
  } else if (!stopValid || !stop) {
    add("REWARD_RISK", "NOT_APPLICABLE", "Needs a valid stop.");
  } else {
    const riskDist = entry.minus(stop).abs();
    for (const tp of input.takeProfits) {
      const d = long ? dec(tp.price).minus(entry) : entry.minus(dec(tp.price));
      rewardRisk.push(d.div(riskDist).toFixed(2));
    }
    const rr1 = new Dec(rewardRisk[0]);
    const min = dec(p.minRewardRisk);
    if (rr1.lte(0)) add("REWARD_RISK", "ESCALATE", "The first target is on the wrong side of entry.", rr1.toFixed(2), min.toFixed(2));
    else if (rr1.lt(min)) add("REWARD_RISK", "ESCALATE", `${rr1.toFixed(2)} to target 1 is below the ${min.toFixed(1)} minimum.`, rr1.toFixed(2), min.toFixed(2));
    else add("REWARD_RISK", "PASS", `${rr1.toFixed(2)} to target 1 (minimum ${min.toFixed(1)}).`, rr1.toFixed(2), min.toFixed(2));
  }
  if (input.context.symbolAssigned) add("SYMBOL_ASSIGNED", "PASS", `${input.instrument.symbol} is on your list.`);
  else if (p.allowUnassignedSymbols) add("SYMBOL_ASSIGNED", "PASS", `${input.instrument.symbol} isn't assigned to you, but this account allows it.`);
  else add("SYMBOL_ASSIGNED", "REJECT", `${input.instrument.symbol} isn't one of your assigned symbols.`);
  if (input.context.inSession) {
    add("SESSION_OPEN", "PASS", "The market session is open.");
  } else if (input.orderType !== "MARKET" && input.context.nextOpenLabel) {
    add("SESSION_OPEN", "PASS", `${input.orderType === "LIMIT" ? "Limit" : "Stop"} order queued for the ${input.context.nextOpenLabel} open.`);
  } else {
    const r = p.outsideSessionAction;
    add("SESSION_OPEN", r, r === "PASS" ? "Outside the session; allowed on this account." : "The market session is closed.");
  }
  if (!p.requireLinkedAnalysis) add("LINKED_ANALYSIS", "NOT_APPLICABLE", "Not required on this account.");
  else add("LINKED_ANALYSIS", input.context.hasLinkedAnalysis ? "PASS" : "ESCALATE", input.context.hasLinkedAnalysis ? "Linked to today's analysis or an active setup." : "Not linked to today's analysis or an active setup.");
  const order = ["SL_REQUIRED", "RISK_PER_TRADE", "OPEN_RISK", "OPEN_TRADES", "DAILY_LOSS_LOCK", "REWARD_RISK", "SYMBOL_ASSIGNED", "SESSION_OPEN", "LINKED_ANALYSIS", "FX_RATE_FRESH"];
  checks.sort((a, b) => order.indexOf(a.code) - order.indexOf(b.code));
  const outcome = checks.some((c) => c.result === "REJECT") ? "REJECT" : checks.some((c) => c.result === "ESCALATE") ? "ESCALATE" : "PASS";
  return {
    outcome,
    riskAmount: riskAmount ? riskAmount.toFixed(2) : null,
    riskPct: riskPct ? riskPct.toFixed(4) : null,
    rewardRisk,
    fxRate: rate ? rate.toString() : null,
    checks
  };
}
function positionSize(args) {
  const dist = dec(args.entry).minus(dec(args.stopLoss)).abs();
  const perUnit = dist.mul(dec(args.pointValue)).mul(dec(args.fxRate ?? 1));
  const equity = dec(args.equity);
  if (perUnit.isZero() || equity.lte(0)) return null;
  const step = dec(args.sizeStep);
  const raw = equity.mul(dec(args.riskPct)).div(100).div(perUnit);
  let size = step.gt(0) ? raw.div(step).floor().mul(step) : raw;
  if (args.minSize !== void 0 && size.lt(dec(args.minSize))) size = new Dec(0);
  const riskAmount = size.mul(perUnit);
  return { size: size.toString(), riskAmount: riskAmount.toFixed(2), riskPct: riskAmount.div(equity).mul(100).toFixed(4) };
}
function rMultiple(direction, entry, price, initialStop) {
  const dist = dec(entry).minus(dec(initialStop)).abs();
  if (dist.isZero()) return null;
  const move = direction === "LONG" ? dec(price).minus(dec(entry)) : dec(entry).minus(dec(price));
  return move.div(dist).toFixed(2);
}

// src/calendar.ts
import { DateTime } from "luxon";
var toDT = (d) => typeof d === "string" ? DateTime.fromISO(d, { zone: "utc" }) : DateTime.fromJSDate(d, { zone: "utc" });
function atLocal(date, hhmm2, zone) {
  if (hhmm2 === "24:00") return DateTime.fromISO(`${date}T00:00`, { zone }).plus({ days: 1 });
  return DateTime.fromISO(`${date}T${hhmm2}`, { zone });
}
function holidayOn(def2, date) {
  return def2.holidays.find((h) => h.date === date) ?? null;
}
function sessionsOn(def2, date) {
  const local = DateTime.fromISO(date, { zone: def2.timezone });
  const hol = holidayOn(def2, date);
  if (hol && !hol.earlyCloseAt) return [];
  return def2.sessions.filter((s) => s.weekday === local.weekday).map((s) => {
    const open = atLocal(date, s.opensAt, def2.timezone);
    let close = atLocal(date, s.closesAt, def2.timezone);
    if (hol?.earlyCloseAt) {
      const early = atLocal(date, hol.earlyCloseAt, def2.timezone);
      if (early < close) close = early;
    }
    return { open: open.toUTC().toJSDate(), close: close.toUTC().toJSDate() };
  }).filter((w) => w.close > w.open).sort((a, b) => a.open.getTime() - b.open.getTime());
}
function localDate(instant, zone) {
  return toDT(instant).setZone(zone).toISODate();
}
function isInSession(def2, instant) {
  const t = toDT(instant).toJSDate().getTime();
  const today = localDate(instant, def2.timezone);
  const yesterday = DateTime.fromISO(today).minus({ days: 1 }).toISODate();
  return [yesterday, today].some((d) => sessionsOn(def2, d).some((w) => w.open.getTime() <= t && t < w.close.getTime()));
}
function nextSessionOpen(def2, instant) {
  const t = toDT(instant);
  let d = t.setZone(def2.timezone).startOf("day");
  for (let i = 0; i < 21; i++) {
    for (const w of sessionsOn(def2, d.toISODate())) {
      if (w.open.getTime() > t.toMillis()) return w.open;
    }
    d = d.plus({ days: 1 });
  }
  return null;
}
function nextOpenAfterClose(def2, instant) {
  let cursor = instant;
  for (let i = 0; i < 30; i++) {
    const open = nextSessionOpen(def2, cursor);
    if (!open) return null;
    const justBefore = new Date(open.getTime() - 1e3);
    if (!isInSession(def2, justBefore)) return open;
    cursor = open;
  }
  return null;
}
function tradingDayOf(def2, instant) {
  const zone = def2.analysisDeadline.mode === "FIXED_TIME" ? def2.analysisDeadline.tz : def2.timezone;
  return localDate(instant, zone);
}
function isAnalysisDay(def2, date) {
  const zone = def2.analysisDeadline.mode === "FIXED_TIME" ? def2.analysisDeadline.tz : def2.timezone;
  const weekday = DateTime.fromISO(date, { zone }).weekday;
  const days = def2.analysisWeekdays ?? [1, 2, 3, 4, 5];
  if (!days.includes(weekday)) return false;
  const hol = holidayOn(def2, date);
  return !(hol && !hol.earlyCloseAt);
}
function analysisDeadline(def2, date) {
  if (!isAnalysisDay(def2, date)) return null;
  const rule = def2.analysisDeadline;
  if (rule.mode === "FIXED_TIME") return atLocal(date, rule.time, rule.tz).toUTC().toJSDate();
  const first = sessionsOn(def2, date)[0];
  if (!first) return null;
  return new Date(first.open.getTime() - rule.minutes * 6e4);
}
var minutesOf = (hhmm2) => {
  const [h, m] = hhmm2.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
function inQuietHours(q, instant) {
  const local = toDT(instant).setZone(q.tz);
  const m = local.hour * 60 + local.minute;
  const s = minutesOf(q.start);
  const e = minutesOf(q.end);
  return s < e ? m >= s && m < e : m >= s || m < e;
}
function quietHoursEnd(q, instant) {
  const local = toDT(instant).setZone(q.tz);
  let end = atLocal(local.toISODate(), q.end, q.tz);
  if (end <= local) end = end.plus({ days: 1 });
  return end.toUTC().toJSDate();
}
var isCovered = (covered, at) => typeof covered === "function" ? covered(at) : !!covered;
function nextProgressDue(a) {
  const from = toDT(a.from);
  let due;
  if (a.anchor === "clock_aligned") {
    const local = from.setZone(a.market.timezone);
    const sinceMidnight = local.diff(local.startOf("day"), "minutes").minutes;
    const slots = Math.floor(sinceMidnight / a.intervalMinutes) + 1;
    due = local.startOf("day").plus({ minutes: slots * a.intervalMinutes }).toUTC();
  } else {
    due = from.plus({ minutes: a.intervalMinutes });
  }
  for (let i = 0; i < 6; i++) {
    let moved = false;
    if (a.sessionOnly && !isInSession(a.market, due.toJSDate())) {
      const open = nextSessionOpen(a.market, due.toJSDate());
      if (open) {
        due = toDT(open);
        moved = true;
      }
    }
    if (a.quietHours && !isCovered(a.covered, due.toJSDate()) && inQuietHours(a.quietHours, due.toJSDate())) {
      due = toDT(quietHoursEnd(a.quietHours, due.toJSDate()));
      moved = true;
    }
    if (!moved) break;
  }
  return due.toJSDate();
}
function dueStages(type, item) {
  const at = (ms) => new Date(ms);
  const due = item.dueAt.getTime();
  switch (type) {
    case "ANALYSIS":
      return [
        { at: at(due - 60 * 6e4), action: "remind" },
        { at: at(due - 15 * 6e4), action: "remind" },
        { at: item.dueAt, action: "miss" }
      ].filter((s) => s.at.getTime() >= item.createdAt.getTime() - 6e4 || s.action === "miss");
    case "PROGRESS_REPORT":
    case "SETUP_UPDATE":
      return [
        { at: item.dueAt, action: "due" },
        { at: item.graceUntil, action: "overdue" },
        { at: item.missAt, action: "miss" }
      ];
    case "CLOSING_REPORT":
      return [
        { at: item.createdAt, action: "due" },
        { at: at(due - 12 * 36e5), action: "remind" },
        { at: item.dueAt, action: "miss" }
      ];
    case "APPROVAL_SLA":
      return [{ at: item.dueAt, action: "escalate" }];
    case "HANDOVER_ACCEPT":
      return [{ at: item.dueAt, action: "expire" }];
    case "WEBINAR_REMINDER":
      return [{ at: item.dueAt, action: "remind" }];
    case "REQUIRED_LEARNING":
      return [
        { at: at(due - 3 * 864e5), action: "remind" },
        { at: at(due - 864e5), action: "remind" },
        { at: item.dueAt, action: "miss" }
      ];
  }
}
function nextSessionClose(def2, instant) {
  const t = toDT(instant).toMillis();
  let d = toDT(instant).setZone(def2.timezone).startOf("day").minus({ days: 1 });
  for (let i = 0; i < 23; i++) {
    for (const w of sessionsOn(def2, d.toISODate())) {
      if (w.close.getTime() > t && !isInSession(def2, w.close)) return w.close;
    }
    d = d.plus({ days: 1 });
  }
  return null;
}
function quietHoursStart(q, instant) {
  const local = toDT(instant).setZone(q.tz);
  let start = atLocal(local.toISODate(), q.start, q.tz);
  if (start > local) start = start.minus({ days: 1 });
  return start.toUTC().toJSDate();
}
function localInputToUtc(value, tz) {
  const dt = DateTime.fromISO(value, { zone: tz });
  return dt.isValid ? dt.toUTC().toJSDate() : null;
}
function utcToLocalInput(instant, tz) {
  return toDT(instant).setZone(tz).toFormat("yyyy-MM-dd'T'HH:mm");
}

// src/settings.ts
import { z } from "zod";
var def = (d) => d;
var SETTINGS = {
  PROGRESS_REPORT_INTERVAL: def({ default: 240, scopes: ["GLOBAL", "MARKET", "ACCOUNT", "TRADE"], schema: z.number().int().min(30).max(1440), description: "Minutes between progress reports on an open trade." }),
  PROGRESS_REPORT_ANCHOR: def({ default: "entry_time", scopes: ["GLOBAL", "MARKET"], schema: z.enum(["entry_time", "clock_aligned"]), description: "Count from entry, or align to clock slots in the market timezone." }),
  PROGRESS_REPORT_GRACE: def({ default: 15, scopes: ["GLOBAL", "MARKET"], schema: z.number().int().min(0).max(240), description: "Minutes after the due time before a report is overdue." }),
  PROGRESS_REPORT_MISS_AFTER: def({ default: 15, scopes: ["GLOBAL", "MARKET"], schema: z.number().int().min(0).max(240), description: "Minutes after the grace period before a report is missed and escalated." }),
  PROGRESS_REPORT_SESSION_ONLY: def({ default: true, scopes: ["GLOBAL", "MARKET"], schema: z.boolean(), description: "Reports falling outside the session are due at the next open." }),
  ANALYSIS_REQUIRES_REVIEW: def({ default: false, scopes: ["GLOBAL", "MARKET"], schema: z.boolean(), description: "Master Trader approves analyses before they are published." }),
  APPROVAL_SLA: def({ default: 10, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(240), description: "Minutes per approval level before escalating." }),
  APPROVAL_VALIDITY: def({ default: 60, scopes: ["GLOBAL"], schema: z.number().int().min(5).max(1440), description: "Minutes an approved plan stays valid for execution." }),
  CLOSING_REPORT_WINDOW: def({ default: 24 * 60, scopes: ["GLOBAL"], schema: z.number().int().min(60).max(7 * 24 * 60), description: "Minutes after close by which the closing report is due." }),
  DAILY_LOSS_WARNING_PCT: def({ default: 80, scopes: ["GLOBAL"], schema: z.number().int().min(10).max(100), description: "Warn when this share of the daily loss limit is used." }),
  FX_RATE_MAX_AGE_DAYS: def({ default: 3, scopes: ["GLOBAL"], schema: z.number().int().min(0).max(30), description: "Older rates make the risk check escalate." }),
  FLOOR_SEES_MONEY_PNL: def({ default: false, scopes: ["GLOBAL"], schema: z.boolean(), description: "Show money amounts to other traders (D8)." }),
  COMMENT_EDIT_WINDOW: def({ default: 15, scopes: ["GLOBAL"], schema: z.number().int().min(0).max(1440), description: "Minutes after posting during which a comment can be edited (INT-ANL-03)." }),
  ACTIVATION_LINK_HOURS: def({ default: 72, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(14 * 24), description: "Hours an approved applicant\u2019s activation link stays valid (INT-ENR-02)." }),
  INVITE_LINK_DAYS: def({ default: 14, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(90), description: "Days an Admin invite link stays valid (INT-ENR-01)." }),
  PASSWORD_RESET_LINK_MINUTES: def({ default: 60, scopes: ["GLOBAL"], schema: z.number().int().min(5).max(24 * 60), description: "Minutes a password reset link stays valid, whether the member asked for it or an Admin made it (INT-ENR-05)." }),
  SETUP_DEFAULT_EXPIRY: def({ default: "end_of_week", scopes: ["GLOBAL", "MARKET"], schema: z.union([z.enum(["end_of_week", "end_of_day"]), z.number().int().min(60).max(60 * 24 * 60)]), description: "When a new setup expires unless the trader picks a time: the end of the market's trading week or day, or a number of minutes after it is marked." }),
  SETUP_UPDATE_GRACE: def({ default: 15, scopes: ["GLOBAL", "MARKET"], schema: z.number().int().min(0).max(240), description: "Minutes after a setup update is due before the trader is reminded." }),
  SETUP_UPDATE_MISS_AFTER: def({ default: 15, scopes: ["GLOBAL", "MARKET"], schema: z.number().int().min(0).max(240), description: "Minutes after the reminder before the setup is marked stale." }),
  HANDOVER_SCOPE: def({ default: "any_trader", scopes: ["GLOBAL"], schema: z.enum(["any_trader", "same_market"]), description: "Who can cover another trader: any active trader (same-market traders suggested first), or same-market traders only." }),
  HANDOVER_ACCEPT_TIMEOUT: def({ default: 30, scopes: ["GLOBAL"], schema: z.number().int().min(5).max(1440), description: "Minutes the covering trader has to accept a handover before the owner is alerted." }),
  REPORTING_CURRENCIES: def({ default: ["USD", "AUD", "PKR"], scopes: ["GLOBAL"], schema: z.array(z.string().regex(/^[A-Z]{3}$/)).min(1).max(12), description: "Currencies offered in the currency switcher (D10)." }),
  FX_RATE_SOURCE: def({ default: "open.er-api", scopes: ["GLOBAL"], schema: z.enum(["open.er-api", "manual"]), description: "Where daily FX rates come from: the free open.er-api.com feed (fetched once a day, Admin overrides still win) or manual entry only." }),
  AUDIT_EXPORT_MAX_ROWS: def({ default: 5e4, scopes: ["GLOBAL"], schema: z.number().int().min(1e3).max(5e5), description: "Most rows one audit-log CSV export may contain (newest first)." }),
  REQUIRED_LEARNING_DEFAULT_DAYS: def({ default: 7, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(90), description: "Days to finish required learning when no due date is set, including onboarding items for new traders (INT-LIB-05, INT-ENR-03)." }),
  STRATEGY_STATS_MIN_TRADES: def({ default: 20, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(1e3), description: "Closed floor trades a strategy needs before its page shows how trades using it performed (INT-LIB-06)." }),
  SIGNAL_DEDUPE_MINUTES: def({ default: 15, scopes: ["GLOBAL"], schema: z.number().int().min(0).max(1440), description: "A TradingView signal repeating the same source, symbol, strategy, timeframe and event within this many minutes is ignored (INT-SYN-02). 0 = keep every repeat." }),
  SIGNAL_WEBHOOK_RATE_LIMIT: def({ default: 120, scopes: ["GLOBAL"], schema: z.number().int().min(10).max(2e3), description: "Webhook calls one signal source may make per minute; more are refused with 429 and logged (INT-SYN-02)." }),
  SIGNAL_FLASH_BARS: def({ default: 2, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(20), description: "A new TradingView signal flashes on the floor feed for this many bars of its own timeframe (e.g. 2 \xD7 H1 = 2 hours), then shows as normal (D27, INT-SYN-02)." }),
  PRICE_SOURCE: def({ default: "yahoo", scopes: ["GLOBAL"], schema: z.enum(["yahoo", "manual"]), description: "Interim delayed prices (D23): Yahoo Finance\u2019s free public endpoint, or manual (no fetching \u2014 traders type prices). PSX and symbols without a price symbol are always manual." }),
  PRICE_POLL_SECONDS: def({ default: 60, scopes: ["GLOBAL"], schema: z.number().int().min(30).max(3600), description: "How often the worker fetches delayed prices, only for symbols with an open trade or active setup while their market is open (INT-SYN-04)." }),
  PRICE_STALE_MINUTES: def({ default: 20, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(1440), description: "A fetched price older than this (by the source\u2019s own time) is labelled stale (INT-SYN-04)." }),
  WEBINAR_REMINDERS: def({ default: [1440, 60, 10], scopes: ["GLOBAL"], schema: z.array(z.number().int().min(1).max(10080)).max(6), description: "Minutes before a webinar starts at which registrants are reminded (default 24 h, 1 h, 10 min). Changing it re-times reminders of webinars that have not started (INT-WEB-02)." }),
  WEBINAR_CAPACITY: def({ default: 100, scopes: ["GLOBAL"], schema: z.number().int().min(1).max(1e3), description: "In-app viewers (traders and guests, not hosts) allowed in one webinar at a time; more are refused with a clear message (INT-WEB-03, D12)." }),
  BRAND_NAME: def({ default: "K5 Trading", scopes: ["GLOBAL"], schema: z.string().trim().min(2).max(60), description: "Name shown on public streams and recordings: the logo badge of the stream overlay and the slates (D21, INT-STR-03)." }),
  BRAND_OPERATOR: def({ default: "K5C Holdings Pty Ltd (ABN 80 624 329 559), trading as K5 Trading", scopes: ["GLOBAL"], schema: z.string().trim().min(2).max(200), description: "Operating entity printed on the start and end slates of public streams (D21, \xA79a)." }),
  STREAM_SHORT_DISCLAIMER: def({ default: "Education only. General information, not personal financial advice. Trading involves risk. \u2014 K5 Trading \xB7 K5C Holdings Pty Ltd", scopes: ["GLOBAL"], schema: z.string().trim().min(10).max(300), description: "Short disclaimer running along the bottom of every public stream (\xA79a). The slates and YouTube descriptions use the full DISCLAIMER document." }),
  STREAM_RETRY_LIMIT: def({ default: 5, scopes: ["GLOBAL"], schema: z.number().int().min(0).max(20), description: "Automatic restarts of a failed streaming destination before it is left in error (INT-STR-04). Retries back off from 30 s to 5 min." }),
  RECORDING_PUBLISH_APPROVER: def({ default: ["ADMIN"], scopes: ["GLOBAL"], schema: z.array(z.enum(["ADMIN", "MASTER_TRADER", "RISK_MANAGER"])).min(1).max(3), description: "Role(s) allowed to approve publishing a recording to YouTube; nothing is published without their approval (D13, INT-REC-02)." }),
  RECORDING_DEFAULT_PRIVACY: def({ default: "public", scopes: ["GLOBAL"], schema: z.enum(["public", "unlisted"]), description: "Privacy pre-selected in a new YouTube publish package; the approver can switch to unlisted or a scheduled time (INT-REC-01)." }),
  RECORDING_YOUTUBE_PLAYLIST: def({ default: "", scopes: ["GLOBAL"], schema: z.string().trim().regex(/^([A-Za-z0-9_-]{10,64})?$/), description: "YouTube playlist id new publish packages add videos to (the part after list= in the playlist link). Empty = no playlist; the approver can change it per video (INT-REC-01)." }),
  RECORDING_PUBLISH_RETRIES: def({ default: 4, scopes: ["GLOBAL"], schema: z.number().int().min(0).max(10), description: "Automatic retries of a failed YouTube publish (backing off 2, 4, 8, 16\u2026 minutes) before the approver and Admins are alerted (INT-REC-03)." })
};
var SCOPE_PRECEDENCE = ["TRADE", "SETUP", "ACCOUNT", "MARKET", "USER", "GLOBAL"];
function resolveSetting(key, context, stored) {
  const d = SETTINGS[key];
  for (const scope of SCOPE_PRECEDENCE) {
    if (!d.scopes.includes(scope)) continue;
    const scopeId = scope === "GLOBAL" ? "*" : context[scope];
    if (scopeId === void 0) continue;
    const hit = stored.find((s) => s.key === key && s.scope === scope && s.scopeId === scopeId);
    if (hit) {
      const parsed = d.schema.safeParse(hit.value);
      if (parsed.success) return parsed.data;
    }
  }
  return d.default;
}

// src/alerts.ts
var info = { critical: false, defaultChannels: ["IN_APP", "TELEGRAM"], digest: false };
var quiet = { critical: false, defaultChannels: ["IN_APP"], digest: true };
var critical = { critical: true, defaultChannels: ["IN_APP", "TELEGRAM", "WEB_PUSH"], digest: false };
var ALERTS = {
  "enrollment.received": info,
  "enrollment.decided": info,
  "analysis.due": info,
  "analysis.missed": info,
  "analysis.published": quiet,
  "analysis.review_needed": info,
  "plan.pending_approval": critical,
  "plan.decided": critical,
  "plan.approval_expiring": info,
  "trade.stop_widened": critical,
  "risk.daily_loss_warning": critical,
  "risk.daily_loss_hit": critical,
  "progress.due": info,
  "progress.overdue": info,
  "progress.missed": critical,
  "closing_report.due": info,
  "closing_report.missed": info,
  "setup.update_due": info,
  "setup.stale": info,
  "setup.status_changed": quiet,
  "setup.expired": info,
  "reminder.fired": info,
  "handover.requested": info,
  "handover.not_accepted": info,
  "handover.started": info,
  "handover.ended": info,
  "trade.uncovered_quiet_hours": quiet,
  "trade.opened": quiet,
  "webinar.reminder": info,
  /** INT-WEB-01: a webinar was scheduled for me (or its time/audience changed, or it was cancelled) */
  "webinar.scheduled": { critical: false, defaultChannels: ["IN_APP", "TELEGRAM"], digest: true },
  "webinar.changed": info,
  /** INT-WEB-02: the host went live */
  "webinar.live": info,
  /** INT-SCR-01/03: someone invited me to a live room (or opened one to discuss my trade, setup or analysis) */
  "room.invited": info,
  /** INT-STR-04: a streaming destination failed (presenter and Admins); it is retried automatically */
  "stream.destination_failed": critical,
  /** INT-REC-01: a YouTube publish package is ready for approval (approvers per RECORDING_PUBLISH_APPROVER) */
  "recording.pending_approval": info,
  /** INT-REC-02: the approver sent the package back to the presenter with notes */
  "recording.changes_requested": info,
  /** INT-REC-03: the recording is on YouTube (presenter and approver) */
  "recording.published": info,
  /** INT-REC-03: publishing failed after the automatic retries (approver and Admins) */
  "recording.publish_failed": critical,
  "comment.mention": { critical: false, defaultChannels: ["IN_APP", "TELEGRAM"], digest: true },
  "comment.added": quiet,
  "symbol.requested": info,
  "symbol.request_decided": info,
  "policy.updated": info,
  "member.suspended": info,
  "handover.accepted": info,
  "fx.fetch_failed": info,
  /** INT-SYN-02: a TradingView strategy signal on a symbol assigned to me (digest-able, quiet hours honoured) */
  "signal.received": { critical: false, defaultChannels: ["IN_APP", "TELEGRAM"], digest: true },
  /** INT-SYN-04: the interim price source keeps failing (Admins, at most once a day) */
  "price.fetch_failed": info,
  "library.new_item": quiet,
  "library.strategy_updated": info,
  "library.required_due": info,
  "learning.assigned": info,
  "learning.overdue": info
};
function channelsFor(key, prefs, inQuiet) {
  const def2 = ALERTS[key];
  let chosen = prefs ?? [...def2.defaultChannels];
  if (def2.critical) {
    if (!chosen.includes("IN_APP")) chosen = ["IN_APP", ...chosen];
    if (!chosen.some((c) => c === "TELEGRAM" || c === "WEB_PUSH")) chosen = [...chosen, "TELEGRAM"];
    return chosen;
  }
  if (inQuiet) return chosen.filter((c) => c === "IN_APP");
  return chosen;
}

// src/schemas.ts
import { IANAZone } from "luxon";
import { z as z4 } from "zod";

// src/signals.ts
import { z as z2 } from "zod";
var SIGNAL_PAYLOAD_VERSION = 1;
var SIGNAL_MAX_BATCH = 50;
var SIGNAL_WEBHOOK_MAX_BYTES = 32 * 1024;
var SIGNAL_EVENTS = ["getting_ready", "ready", "triggered", "invalidated", "info"];
var signalEventCode = (e) => e.toUpperCase();
var SIGNAL_EVENT_LABEL = { GETTING_READY: "Getting ready", READY: "Ready", TRIGGERED: "Triggered", INVALIDATED: "Invalidated", INFO: "Info" };
var SIGNAL_REJECTION_REASONS = ["unknown_token", "revoked", "rate_limited", "too_large", "invalid_json", "invalid_payload", "unknown_symbol", "ambiguous_symbol"];
var SIGNAL_REJECTION_LABEL = {
  unknown_token: "Unknown webhook URL",
  revoked: "Source revoked",
  rate_limited: "Too many requests",
  too_large: "Body too large",
  invalid_json: "Not valid JSON",
  invalid_payload: "Payload not understood",
  unknown_symbol: "Symbol not mapped",
  ambiguous_symbol: "Symbol matches several instruments"
};
var signalPrice = z2.union([z2.number(), z2.string()]).transform((v, ctx) => {
  const s = typeof v === "number" ? Number.isFinite(v) ? String(v) : "" : v.trim().replace(/,/g, "");
  if (!/^\d+(\.\d+)?(e[-+]?\d+)?$/i.test(s) || dec(s).lte(0)) {
    ctx.addIssue({ code: "custom", message: "Expected a positive price" });
    return z2.NEVER;
  }
  return dec(s).toDecimalPlaces(8).toFixed();
});
var optional = (t) => t.nullish().transform((v) => v === null ? void 0 : v);
var signalTime = z2.union([z2.number(), z2.string()]).transform((v, ctx) => {
  let d = null;
  if (typeof v === "number" && Number.isFinite(v)) d = new Date(v < 1e11 ? v * 1e3 : v);
  else if (typeof v === "string" && /^\d{9,13}$/.test(v.trim())) d = new Date(Number(v) < 1e11 ? Number(v) * 1e3 : Number(v));
  else if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.test(v.trim())) d = new Date(v.trim());
  if (!d || Number.isNaN(d.getTime()) || d.getUTCFullYear() < 2e3 || d.getUTCFullYear() > 2100) {
    ctx.addIssue({ code: "custom", message: "Expected an ISO date-time with a timezone, or milliseconds since 1970" });
    return z2.NEVER;
  }
  return d;
});
var FSB_GRADES = ["BLUE", "RED", "YELLOW", "NOBLE"];
var FSB_LABEL = { BLUE: "FSB Blue", RED: "FSB Red", YELLOW: "FSB Yellow", NOBLE: "FSB Noble" };
var SIGNAL_CONFIDENCES = ["HIGH", "MEDIUM", "LOW"];
var ROYAL_REFS = ["LAST", "SECOND_LAST"];
var ROYAL_LABEL = { LAST: "Last Royal", SECOND_LAST: "Second-last Royal" };
var SIGNAL_MAX_BARS_AGO = 1e5;
var word = (v) => typeof v === "string" ? v.trim().toUpperCase().replace(/[\s-]+/g, "_") : null;
function readFsbGrade(v) {
  const w = word(v)?.replace(/^FSB_?/, "");
  return w && FSB_GRADES.includes(w) ? w : null;
}
function readConfidence(v) {
  const w = word(v);
  return w && SIGNAL_CONFIDENCES.includes(w) ? w : null;
}
function readRoyalRef(v) {
  const w = word(v);
  return w && ROYAL_REFS.includes(w) ? w : null;
}
function readBarsAgo(v) {
  const n = typeof v === "number" ? v : typeof v === "string" && /^\s*\d+(\.0+)?\s*$/.test(v) ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 0 && n <= SIGNAL_MAX_BARS_AGO ? n : null;
}
var lenient = (read) => z2.unknown().optional().transform((v) => read(v) ?? void 0);
var signalPayloadSchema = z2.object({
  v: z2.literal(SIGNAL_PAYLOAD_VERSION, { message: `Set "v": ${SIGNAL_PAYLOAD_VERSION} (payload version)` }),
  strategy: z2.string().trim().min(1, 'Name the strategy, e.g. "spring"').max(60),
  event: z2.preprocess((v) => typeof v === "string" ? v.trim().toLowerCase().replace(/[\s-]+/g, "_") : v, z2.enum(SIGNAL_EVENTS, { message: `Use one of: ${SIGNAL_EVENTS.join(", ")}` })),
  symbol: z2.string().trim().min(1).max(64).transform((s) => s.toUpperCase()),
  tf: z2.union([z2.string(), z2.number()]).transform((v) => String(v).trim()).pipe(z2.string().min(1, "Give the timeframe (TradingView interval, e.g. 15, 60, 1D)").max(16)),
  direction: optional(z2.preprocess((v) => typeof v === "string" ? v.trim().toUpperCase() : v, z2.enum(["LONG", "SHORT"], { message: "Use LONG or SHORT" }))),
  price: optional(signalPrice),
  entry: optional(signalPrice),
  stop: optional(signalPrice),
  targets: optional(z2.array(signalPrice).max(5, "At most 5 targets")),
  notes: optional(z2.string().trim().max(1e3)),
  time: optional(signalTime),
  // optional structured fields (Signals table); junk → not sent, never a refusal
  fsb: lenient(readFsbGrade),
  fsbBarsAgo: lenient(readBarsAgo),
  royalBarsAgo: lenient(readBarsAgo),
  confidence: lenient(readConfidence),
  breakoutBarsAgo: lenient(readBarsAgo),
  royal: lenient(readRoyalRef)
});
function signalExtras(raw) {
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return {
    fsbGrade: readFsbGrade(o.fsb),
    fsbBarsAgo: readBarsAgo(o.fsbBarsAgo),
    royalBarsAgo: readBarsAgo(o.royalBarsAgo),
    confidence: readConfidence(o.confidence),
    breakoutBarsAgo: readBarsAgo(o.breakoutBarsAgo),
    royalRef: readRoyalRef(o.royal)
  };
}
function barsElapsed(barTime, now, tf) {
  const bar = timeframeMs(vtfTimeframe(tf)) ?? timeframeMs(tf);
  if (!bar) return null;
  const t = new Date(barTime).getTime();
  const n = typeof now === "number" ? now : now.getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((n - t) / bar));
}
function barsAgoNow(barsAgo, barTime, now, tf) {
  if (barsAgo == null) return null;
  const elapsed = barsElapsed(barTime, now, tf);
  return elapsed === null ? null : barsAgo + elapsed;
}
var SIGNAL_TABLE_SORTS = ["time", "received", "symbol", "exchange", "market", "price", "signalPrice", "direction", "barsAway", "fsb", "confidence", "breakout", "event", "tf", "strategy"];
var SIGNAL_TABLE_MAX_LIMIT = 500;
var blankToUndefined = (v) => typeof v === "string" && v.trim() === "" ? void 0 : v;
var signalTableQuerySchema = z2.object({
  /** latest = newest signal per symbol × timeframe × strategy; all = every signal */
  view: z2.preprocess(blankToUndefined, z2.enum(["latest", "all"]).default("latest")),
  /** market id or code */
  market: z2.preprocess(blankToUndefined, z2.string().trim().max(40).optional()),
  strategy: z2.preprocess(blankToUndefined, z2.string().trim().max(60).optional()),
  /** VTF timeframe label (M5, H1, D1) or TradingView interval */
  tf: z2.preprocess(blankToUndefined, z2.string().trim().max(16).optional()),
  event: z2.preprocess((v) => typeof v === "string" ? v.trim() === "" ? void 0 : v.trim().toLowerCase().replace(/[\s-]+/g, "_") : v, z2.enum(SIGNAL_EVENTS).optional()),
  sort: z2.preprocess(blankToUndefined, z2.enum(SIGNAL_TABLE_SORTS).default("time")),
  dir: z2.preprocess(blankToUndefined, z2.enum(["asc", "desc"]).default("desc")),
  limit: z2.preprocess(blankToUndefined, z2.coerce.number().int().min(1).max(SIGNAL_TABLE_MAX_LIMIT).default(200))
});
function parseWebhookBody(raw) {
  const text3 = (raw ?? "").replace(/^﻿/, "").trim();
  if (!text3) return { ok: false, reason: "invalid_json", message: "The request body is empty. Put the signal JSON in the alert message." };
  let json;
  try {
    json = JSON.parse(text3);
  } catch (e) {
    const unfilled = /\{\{\w+\}\}/.test(text3) ? " (a {{placeholder}} was not filled in \u2014 build the message with alert() in Pine instead)" : "";
    return { ok: false, reason: "invalid_json", message: `The alert message is not valid JSON${unfilled}: ${e instanceof Error ? e.message : String(e)}`.slice(0, 480) };
  }
  if (!json || typeof json !== "object" || Array.isArray(json)) return { ok: false, reason: "invalid_payload", message: 'Send one signal object, or { "signals": [ \u2026 ] } for a batch.' };
  if ("signals" in json) {
    const list = json.signals;
    if (!Array.isArray(list) || list.length === 0) return { ok: false, reason: "invalid_payload", message: '"signals" must be a non-empty array.' };
    if (list.length > SIGNAL_MAX_BATCH) return { ok: false, reason: "invalid_payload", message: `At most ${SIGNAL_MAX_BATCH} signals per request (got ${list.length}).` };
    return { ok: true, batch: true, items: list };
  }
  return { ok: true, batch: false, items: [json] };
}
function signalIssues(error) {
  return error.issues.map((i) => `${i.path.join(".") || "signal"}: ${i.message}`).join("; ").slice(0, 480);
}
function splitTicker(ticker) {
  let t = ticker.trim().toUpperCase();
  if (t.startsWith("={")) {
    try {
      const inner = JSON.parse(ticker.trim().slice(1));
      if (typeof inner.symbol === "string") t = inner.symbol.trim().toUpperCase();
    } catch {
    }
  }
  const i = t.indexOf(":");
  return i < 0 ? { full: t, exchange: null, plain: t } : { full: t, exchange: t.slice(0, i), plain: t.slice(i + 1) };
}
var tvTickerSchema = z2.string().trim().toUpperCase().max(64).refine((v) => v === "" || /^([A-Z0-9_.]+:)?[A-Z0-9_.!/&-]+$/.test(v), "Use a TradingView ticker such as OANDA:XAUUSD, NASDAQ:AAPL or PSX:OGDC").transform((v) => v === "" ? null : v);
function vtfTimeframe(tf) {
  const t = tf.trim().toUpperCase();
  const minutes = /^(\d+)$/.exec(t);
  if (minutes) {
    const n = Number(minutes[1]);
    return n >= 60 && n % 60 === 0 ? `H${n / 60}` : `M${n}`;
  }
  const unit = /^(\d*)([SDWM])$/.exec(t);
  if (unit) {
    const n = unit[1] ? Number(unit[1]) : 1;
    return `${unit[2] === "M" ? "MN" : unit[2]}${n}`;
  }
  return t.slice(0, 10);
}
var SIGNAL_FLASH_FALLBACK_MS = 15 * 6e4;
function timeframeMs(label) {
  const m = /^(S|M|H|D|W|MN)(\d+)$/.exec(label.trim().toUpperCase());
  if (!m) return null;
  const n = Number(m[2]);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = { S: 1e3, M: 6e4, H: 36e5, D: 864e5, W: 7 * 864e5, MN: 30 * 864e5 }[m[1]];
  return n * unit;
}
function signalFlashUntil(receivedAt, tf, bars) {
  const at = typeof receivedAt === "string" ? new Date(receivedAt) : receivedAt;
  const bar = timeframeMs(vtfTimeframe(tf)) ?? timeframeMs(tf) ?? SIGNAL_FLASH_FALLBACK_MS;
  return new Date(at.getTime() + Math.max(0, Math.floor(bars)) * bar);
}
function signalSetupDraft(s) {
  const zone = s.entry ?? s.price;
  const what = `${s.strategy} \xB7 ${SIGNAL_EVENT_LABEL[s.event].toLowerCase()}`;
  return {
    signalId: s.id,
    instrumentId: s.instrumentId,
    direction: s.direction,
    timeframe: vtfTimeframe(s.tf),
    zoneLow: zone,
    zoneHigh: null,
    invalidationPrice: s.stop,
    targetIdea: s.targets.length ? s.targets.join(" / ") : null,
    triggerCondition: `TradingView signal from ${s.sourceName}: ${what} on ${s.tf}${s.notes ? ` \u2014 ${s.notes}` : ""}`.slice(0, 2e3)
  };
}
var signalSourceCreateSchema = z2.object({ name: z2.string().trim().min(2, 'Give the source a name, e.g. "Wyckoff scanner"').max(80) });

// src/prices.ts
import { DateTime as DateTime2 } from "luxon";
import { z as z3 } from "zod";
var PRICE_SOURCES = {
  yahoo: { id: "yahoo", name: "Yahoo Finance", short: "Yahoo", note: "Unofficial public chart endpoint; quotes can be delayed (futures typically 10\u201330 min)." }
};
var priceSourceName = (id5) => PRICE_SOURCES[id5]?.short ?? id5;
var priceProxyNote = (sourceSymbol) => sourceSymbol && /=F$/i.test(sourceSymbol) ? `${sourceSymbol} futures price as a proxy` : null;
var priceSymbolSchema = z3.string().trim().max(32).refine((v) => v === "" || /^[A-Za-z0-9^=._-]+$/.test(v), "Use the price source\u2019s symbol, e.g. EURUSD=X, GC=F or AAPL").transform((v) => v === "" ? null : v.toUpperCase());
function priceAge(asOf, now, staleMinutes) {
  const minutes = Math.max(0, Math.floor(((typeof now === "number" ? now : now.getTime()) - new Date(asOf).getTime()) / 6e4));
  return { minutes, stale: minutes > staleMinutes };
}
function priceLabel(mark, tz, now = Date.now()) {
  const at = DateTime2.fromJSDate(new Date(mark.asOf)).setZone(tz);
  const today = DateTime2.fromMillis(typeof now === "number" ? now : now.getTime()).setZone(tz).hasSame(at, "day");
  return `${mark.stale ? "Stale" : "Delayed"} \xB7 ${priceSourceName(mark.source)} \xB7 ${today ? at.toFormat("HH:mm") : at.toFormat("ccc d LLL HH:mm")}`;
}
function markToMarket(t, price) {
  const entry = dec(t.avgEntry);
  const p = dec(price);
  const move = t.direction === "LONG" ? p.minus(entry) : entry.minus(p);
  const pct3 = entry.isZero() ? null : move.div(entry).mul(100).toFixed(2);
  const r = t.initialStop ? rMultiple(t.direction, entry, p, t.initialStop) : null;
  const pnl = t.quoteToAccount === null ? null : move.mul(dec(t.pointValue)).mul(dec(t.sizeOpen)).mul(dec(t.quoteToAccount)).toFixed(2);
  return { r, pct: pct3, pnl };
}

// src/schemas.ts
var decimalString = z4.union([z4.string(), z4.number()]).transform((v) => String(v).trim()).pipe(z4.string().regex(/^-?\d+(\.\d+)?$/, "Enter a number"));
var positiveDecimal = decimalString.refine((v) => Number(v) > 0, "Must be greater than zero");
var ROLES = ["ADMIN", "MASTER_TRADER", "RISK_MANAGER", "TRADER"];
var loginSchema = z4.object({ email: z4.string().trim().toLowerCase().email(), password: z4.string().min(1) });
var totpCodeSchema = z4.object({ challenge: z4.string().min(10), code: z4.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code") });
var totpEnrolSchema = z4.object({ challenge: z4.string().min(10), code: z4.string().trim().regex(/^\d{6}$/) });
var createUserSchema = z4.object({
  email: z4.string().trim().toLowerCase().email(),
  name: z4.string().trim().min(2).max(120),
  roles: z4.array(z4.enum(ROLES)).min(1),
  timezone: z4.string().min(3).default("Asia/Karachi"),
  initialPassword: z4.string().min(12, "At least 12 characters")
});
var updateMeSchema = z4.object({
  timezone: z4.string().min(3).optional(),
  reportingCurrency: z4.string().length(3).optional(),
  quietHoursEnabled: z4.boolean().optional(),
  quietHoursStart: z4.string().regex(/^\d{2}:\d{2}$/).optional(),
  quietHoursEnd: z4.string().regex(/^\d{2}:\d{2}$/).optional()
});
var accountCreateSchema = z4.object({
  code: z4.string().trim().min(2).max(20),
  name: z4.string().trim().min(2).max(120),
  marketId: z4.string().min(1),
  currency: z4.string().length(3).toUpperCase(),
  startingEquity: positiveDecimal,
  brokerName: z4.string().max(120).optional(),
  riskPolicyId: z4.string().optional()
});
var assignAccountSchema = z4.object({ userId: z4.string().min(1) });
var assignSymbolsSchema = z4.object({ userId: z4.string().min(1), instrumentIds: z4.array(z4.string().min(1)).min(1) });
var instrumentSchema = z4.object({
  marketId: z4.string().min(1),
  symbol: z4.string().trim().toUpperCase().min(1).max(20),
  displayName: z4.string().trim().min(1).max(120),
  quoteCurrency: z4.string().length(3).toUpperCase(),
  tickSize: positiveDecimal,
  pointValue: positiveDecimal,
  sizeUnit: z4.enum(["SHARES", "LOTS", "CONTRACTS", "UNITS"]),
  minSize: decimalString.default("0"),
  sizeStep: positiveDecimal.default("1"),
  /** INT-SYN-02: TradingView ticker signals arrive with (empty = match on the plain symbol) */
  tvTicker: tvTickerSchema.optional(),
  /** INT-SYN-04: symbol at the interim price source (empty = manual prices only) */
  priceSymbol: priceSymbolSchema.optional()
});
var instrumentUpdateSchema = z4.object({
  displayName: z4.string().trim().min(1).max(120),
  quoteCurrency: z4.string().length(3).toUpperCase(),
  tickSize: positiveDecimal,
  pointValue: positiveDecimal,
  sizeUnit: z4.enum(["SHARES", "LOTS", "CONTRACTS", "UNITS"]),
  minSize: decimalString,
  sizeStep: positiveDecimal,
  active: z4.boolean(),
  tvTicker: tvTickerSchema,
  priceSymbol: priceSymbolSchema
}).partial();
var INSTRUMENT_CSV_COLUMNS = ["symbol", "displayName", "quoteCurrency", "tickSize", "pointValue", "sizeUnit", "minSize", "sizeStep", "tvTicker", "priceSymbol"];
var hhmm = z4.string().trim().regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, "Use 24-hour HH:MM");
var ianaZone = z4.string().trim().refine((v) => IANAZone.isValidZone(v), "Not a known timezone (e.g. Asia/Karachi)");
var isoDate = z4.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
var marketSessionSchema = z4.object({ weekday: z4.number().int().min(1).max(7), opensAt: hhmm, closesAt: hhmm, label: z4.string().trim().max(40).optional() }).refine((s) => s.closesAt > s.opensAt, { message: "Closes before it opens (split overnight sessions at 24:00)", path: ["closesAt"] });
var analysisDeadlineSchema = z4.discriminatedUnion("mode", [
  z4.object({ mode: z4.literal("BEFORE_OPEN"), minutes: z4.number().int().min(0).max(720) }),
  z4.object({ mode: z4.literal("FIXED_TIME"), time: hhmm, tz: ianaZone })
]);
var marketCreateBase = z4.object({
  code: z4.string().trim().toUpperCase().regex(/^[A-Z0-9-]{2,12}$/, "2\u201312 letters, digits or hyphens"),
  name: z4.string().trim().min(2).max(80),
  timezone: ianaZone,
  currency: z4.string().trim().length(3).toUpperCase(),
  is24h: z4.boolean().default(false),
  analysisDeadline: analysisDeadlineSchema,
  sessions: z4.array(marketSessionSchema).max(28)
});
var blankToNull = z4.union([z4.literal(""), z4.null()]).transform(() => null);
var optionalPositive = blankToNull.or(positiveDecimal);
var marketInstrumentDefaultsSchema = z4.object({
  tickSize: optionalPositive,
  pointValue: optionalPositive,
  sizeUnit: blankToNull.or(z4.enum(["SHARES", "LOTS", "CONTRACTS", "UNITS"])),
  minSize: blankToNull.or(decimalString.refine((v) => Number(v) >= 0, "Must be zero or more")),
  sizeStep: optionalPositive
}).partial();
var marketCreateSchema = marketCreateBase.extend({ instrumentDefaults: marketInstrumentDefaultsSchema.optional() });
var marketUpdateSchema = marketCreateSchema.omit({ code: true }).partial().extend({ active: z4.boolean().optional() });
var marketHolidaySchema = z4.object({ date: isoDate, name: z4.string().trim().min(2).max(80), earlyCloseAt: hhmm.nullable().optional() });
var marketHolidaysSchema = z4.object({ holidays: z4.array(marketHolidaySchema).min(1).max(100) });
var fxRateSchema = z4.object({
  currency: z4.string().length(3).toUpperCase(),
  rateDate: z4.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  unitsPerUsd: positiveDecimal
});
var keyLevel = z4.object({ label: z4.string().max(60), price: decimalString, kind: z4.string().max(30) });
var scenario = z4.object({ when: z4.string().max(500), outcome: z4.string().max(500) });
var marketProfile = z4.object({
  vah: decimalString.optional(),
  val: decimalString.optional(),
  poc: decimalString.optional(),
  ibHigh: decimalString.optional(),
  ibLow: decimalString.optional(),
  openingType: z4.enum(["OPEN_DRIVE", "OPEN_TEST_DRIVE", "OPEN_REJECTION_REVERSE", "OPEN_AUCTION"]).optional(),
  dayType: z4.enum(["NORMAL", "NORMAL_VARIATION", "TREND", "DOUBLE_DISTRIBUTION", "NEUTRAL"]).optional()
}).partial();
var analysisSectionSchema = z4.object({
  instrumentId: z4.string().min(1),
  noView: z4.boolean().default(false),
  noViewReason: z4.string().max(2e3).optional().nullable(),
  bias: z4.enum(["BULLISH", "BEARISH", "NEUTRAL", "NO_VIEW"]).optional().nullable(),
  confidence: z4.enum(["LOW", "MEDIUM", "HIGH"]).optional().nullable(),
  htfContext: z4.string().max(5e3).optional().nullable(),
  keyLevels: z4.array(keyLevel).max(30).default([]),
  marketProfile: marketProfile.optional().nullable(),
  scenarios: z4.array(scenario).max(10).default([]),
  events: z4.string().max(2e3).optional().nullable(),
  notes: z4.string().max(5e3).optional().nullable()
});
var analysisSaveSchema = z4.object({ sections: z4.array(analysisSectionSchema).max(50) });
var takeProfitSchema = z4.object({ price: positiveDecimal, sizePct: z4.number().min(1).max(100).default(100) });
var tradePlanSchema = z4.object({
  accountId: z4.string().min(1),
  instrumentId: z4.string().min(1),
  direction: z4.enum(["LONG", "SHORT"]),
  orderType: z4.enum(["MARKET", "LIMIT", "STOP"]).default("MARKET"),
  plannedEntry: positiveDecimal,
  stopLoss: positiveDecimal.optional().nullable(),
  takeProfits: z4.array(takeProfitSchema).max(5).default([]),
  size: positiveDecimal,
  timeframe: z4.string().max(10).optional().nullable(),
  analysisId: z4.string().optional().nullable(),
  setupId: z4.string().optional().nullable(),
  rationale: z4.string().max(5e3).optional().nullable()
});
var approvalDecisionSchema = z4.object({
  decision: z4.enum(["APPROVE", "APPROVE_WITH_CHANGES", "REJECT", "OVERRIDE"]),
  reason: z4.string().max(2e3).optional(),
  size: positiveDecimal.optional()
}).refine((v) => v.decision === "APPROVE" || v.reason && v.reason.trim().length >= 3, { message: "A reason is required", path: ["reason"] }).refine((v) => v.decision !== "APPROVE_WITH_CHANGES" || v.size, { message: "Give the approved size", path: ["size"] });
var executeSchema = z4.object({
  price: positiveDecimal,
  size: positiveDecimal.optional(),
  stopLoss: positiveDecimal.optional(),
  fees: decimalString.optional(),
  brokerRef: z4.string().max(80).optional(),
  occurredAt: z4.coerce.date().optional()
});
var tradeEventSchema = z4.object({
  type: z4.enum(["SCALE_IN", "PARTIAL_CLOSE", "SL_MOVED", "TP_MOVED", "NOTE"]),
  price: positiveDecimal.optional(),
  size: positiveDecimal.optional(),
  stopLoss: positiveDecimal.optional(),
  takeProfits: z4.array(takeProfitSchema).max(5).optional(),
  fees: decimalString.optional(),
  note: z4.string().max(2e3).optional(),
  occurredAt: z4.coerce.date().optional()
});
var closeTradeSchema = z4.object({ price: positiveDecimal, fees: decimalString.optional(), occurredAt: z4.coerce.date().optional() });
var progressReportSchema = z4.object({
  currentPrice: positiveDecimal,
  thesis: z4.enum(["VALID", "WEAKENING", "INVALID"]),
  action: z4.enum(["HOLD", "MOVE_SL", "PARTIAL", "SCALE", "CLOSE"]),
  notes: z4.string().max(5e3).optional(),
  adHoc: z4.boolean().default(false)
});
var closingReportSchema = z4.object({
  followedPlan: z4.boolean(),
  followedPlanNote: z4.string().max(5e3).optional(),
  wentWell: z4.string().max(5e3).optional(),
  improve: z4.string().max(5e3).optional()
});
var settingWriteSchema = z4.object({
  key: z4.string(),
  scope: z4.enum(["GLOBAL", "MARKET", "ACCOUNT", "TRADE", "SETUP", "USER"]),
  scopeId: z4.string().default("*"),
  value: z4.unknown()
});

// src/markets.ts
var weekdays = (from, to, opensAt, closesAt) => Array.from({ length: to - from + 1 }, (_, i) => ({ weekday: from + i, opensAt, closesAt }));
var SHARE_DEFAULTS = { tickSize: "0.01", pointValue: "1", sizeUnit: "SHARES", minSize: "1", sizeStep: "1" };
var SEED_MARKETS = [
  {
    code: "PSX",
    name: "Pakistan Stock Exchange",
    currency: "PKR",
    timezone: "Asia/Karachi",
    is24h: false,
    sessions: [...weekdays(1, 4, "09:30", "15:30"), { weekday: 5, opensAt: "09:15", closesAt: "12:00" }, { weekday: 5, opensAt: "14:30", closesAt: "16:30" }],
    holidays: [],
    analysisDeadline: { mode: "BEFORE_OPEN", minutes: 30 },
    instrumentDefaults: SHARE_DEFAULTS
  },
  {
    code: "US",
    name: "US Equities",
    currency: "USD",
    timezone: "America/New_York",
    is24h: false,
    sessions: weekdays(1, 5, "09:30", "16:00"),
    holidays: [],
    analysisDeadline: { mode: "BEFORE_OPEN", minutes: 30 },
    instrumentDefaults: SHARE_DEFAULTS
  },
  {
    code: "FX",
    name: "Forex",
    currency: "USD",
    timezone: "America/New_York",
    is24h: true,
    sessions: [{ weekday: 7, opensAt: "17:00", closesAt: "24:00" }, ...weekdays(1, 4, "00:00", "24:00"), { weekday: 5, opensAt: "00:00", closesAt: "17:00" }],
    holidays: [],
    analysisDeadline: { mode: "FIXED_TIME", time: "07:00", tz: "Europe/London" },
    instrumentDefaults: { tickSize: "0.00001", pointValue: "100000", sizeUnit: "LOTS", minSize: "0.01", sizeStep: "0.01" }
  },
  {
    code: "COMM",
    name: "Commodities",
    currency: "USD",
    timezone: "America/New_York",
    is24h: true,
    sessions: [
      { weekday: 7, opensAt: "18:00", closesAt: "24:00" },
      ...[1, 2, 3, 4].flatMap((d) => [
        { weekday: d, opensAt: "00:00", closesAt: "17:00" },
        { weekday: d, opensAt: "18:00", closesAt: "24:00" }
      ]),
      { weekday: 5, opensAt: "00:00", closesAt: "17:00" }
    ],
    holidays: [],
    analysisDeadline: { mode: "FIXED_TIME", time: "07:00", tz: "Europe/London" },
    instrumentDefaults: { tickSize: "0.01", pointValue: "100", sizeUnit: "LOTS", minSize: "0.01", sizeStep: "0.01" }
  }
];

// src/csv.ts
function parseCsv(text3) {
  const rows = [];
  let row = [];
  let cell2 = "";
  let quoted = false;
  const src = text3.replace(/^﻿/, "");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        cell2 += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell2 += c;
    } else if (c === '"' && cell2.trim() === "") {
      quoted = true;
      cell2 = "";
    } else if (c === ",") {
      row.push(cell2.trim());
      cell2 = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(cell2.trim());
      if (row.some((x) => x !== "")) rows.push(row);
      row = [];
      cell2 = "";
    } else cell2 += c;
  }
  row.push(cell2.trim());
  if (row.some((x) => x !== "")) rows.push(row);
  return rows;
}
function csvCell(v) {
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

// src/digest.ts
import { DateTime as DateTime3 } from "luxon";
var DIGEST_HOURS = [8, 13, 18];
function nextDigestAt(now, tz, hours = DIGEST_HOURS) {
  const local = DateTime3.fromJSDate(now, { zone: tz });
  for (let d = 0; d <= 1; d++) {
    for (const h of [...hours].sort((a, b) => a - b)) {
      const t = local.startOf("day").plus({ days: d }).set({ hour: h, minute: 0, second: 0, millisecond: 0 });
      if (t > local) return t.toUTC().toJSDate();
    }
  }
  throw new Error("No digest hours configured");
}

// src/reminders.ts
import { DateTime as DateTime4, IANAZone as IANAZone2 } from "luxon";
import { z as z5 } from "zod";
var REMINDER_REPEATS = ["NONE", "DAILY", "WEEKDAYS", "WEEKLY"];
var SNOOZE_MINUTES = [5, 15, 60];
var localDateTime = z5.string().trim().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "Use a date and time");
var reminderSchema = z5.object({
  title: z5.string().trim().min(2, "Say what to remind you about").max(255),
  note: z5.string().trim().max(2e3).optional(),
  /** first time, as local wall-clock time in `timezone` */
  at: localDateTime,
  timezone: z5.string().trim().refine((v) => IANAZone2.isValidZone(v), "Not a known timezone"),
  repeat: z5.enum(REMINDER_REPEATS).default("NONE"),
  instrumentId: z5.string().min(1).optional(),
  setupId: z5.string().min(1).optional(),
  tradeId: z5.string().min(1).optional(),
  accountId: z5.string().min(1).optional()
});
var reminderUpdateSchema = reminderSchema.partial();
var snoozeSchema = z5.object({ minutes: z5.union([z5.literal(5), z5.literal(15), z5.literal(60)]) });
function reminderInstant(at, timezone) {
  return DateTime4.fromISO(at, { zone: timezone }).toUTC().toJSDate();
}
function nextOccurrence(repeat, first, timezone, after) {
  const start = DateTime4.fromJSDate(first, { zone: timezone });
  if (repeat === "NONE") return first > after ? first : null;
  const limit = DateTime4.fromJSDate(after, { zone: timezone });
  if (start > limit && (repeat !== "WEEKDAYS" || start.weekday <= 5)) return first;
  const { hour, minute } = start;
  let day = (start > limit ? start : limit).startOf("day");
  for (let i = 0; i < 400; i++) {
    const t = day.set({ hour, minute, second: 0, millisecond: 0 });
    const okDay = repeat === "DAILY" || repeat === "WEEKDAYS" && t.weekday <= 5 || repeat === "WEEKLY" && t.weekday === start.weekday;
    if (okDay && t > limit && t >= start) return t.toUTC().toJSDate();
    day = day.plus({ days: 1 });
  }
  return null;
}

// src/social.ts
import { z as z6 } from "zod";
var DISCUSSABLE_TARGETS = ["ANALYSIS", "SETUP", "TRADE", "PROGRESS_REPORT", "CLOSING_REPORT"];
var FOLLOW_TARGETS = ["USER", "INSTRUMENT", "MARKET"];
var REACTION_KINDS = ["AGREE", "DISAGREE", "LIKE"];
var FEED_TABS = ["all", "following"];
var FEED_TYPES = ["analyses", "trades", "reports", "setups", "signals", "library"];
var COMMENT_MAX_LENGTH = 4e3;
var sectionTargetId = (analysisId, instrumentId) => `${analysisId}:${instrumentId}`;
function parseAnalysisTarget(targetId2) {
  const i = targetId2.indexOf(":");
  return i < 0 ? { analysisId: targetId2, instrumentId: null } : { analysisId: targetId2.slice(0, i), instrumentId: targetId2.slice(i + 1) };
}
function allowedReactions(targetType, targetId2) {
  if (targetType === "ANALYSIS" && parseAnalysisTarget(targetId2).instrumentId) return ["AGREE", "DISAGREE"];
  return ["LIKE"];
}
var OPPOSITE_REACTION = { AGREE: "DISAGREE", DISAGREE: "AGREE" };
function commentEditableUntil(createdAt, windowMinutes) {
  return new Date(new Date(createdAt).getTime() + windowMinutes * 6e4);
}
var targetId = z6.string().trim().min(1).max(120);
var body = z6.string().trim().min(1, "Write something first").max(COMMENT_MAX_LENGTH, `Keep comments under ${COMMENT_MAX_LENGTH} characters`);
var targetRefSchema = z6.object({ targetType: z6.enum(DISCUSSABLE_TARGETS), targetId });
var commentCreateSchema = z6.object({
  targetType: z6.enum(DISCUSSABLE_TARGETS),
  targetId,
  parentId: z6.string().trim().min(1).max(40).optional().nullable(),
  body
});
var commentEditSchema = z6.object({ body });
var commentHideSchema = z6.object({ reason: z6.string().trim().max(500).optional() });
var reactionToggleSchema = z6.object({ targetType: z6.enum(DISCUSSABLE_TARGETS), targetId, kind: z6.enum(REACTION_KINDS) }).refine((v) => allowedReactions(v.targetType, v.targetId).includes(v.kind), { message: "That reaction is not available here", path: ["kind"] });
var readReceiptSchema = z6.object({ targetType: z6.literal("ANALYSIS"), targetId });
var followSchema = z6.object({ targetType: z6.enum(FOLLOW_TARGETS), targetId });
var targetKey = (type, id5) => `${type}:${id5}`;
function parseTargetKey(key) {
  const i = key.indexOf(":");
  if (i < 0) return null;
  const type = key.slice(0, i);
  const id5 = key.slice(i + 1);
  return DISCUSSABLE_TARGETS.includes(type) && id5 ? { targetType: type, targetId: id5 } : null;
}
var feedQuerySchema = z6.object({
  tab: z6.enum(FEED_TABS).default("all"),
  marketId: z6.string().trim().min(1).optional(),
  instrumentId: z6.string().trim().min(1).optional(),
  traderId: z6.string().trim().min(1).optional(),
  type: z6.enum(FEED_TYPES).optional(),
  q: z6.string().trim().max(100).optional(),
  before: z6.coerce.date().optional(),
  beforeId: z6.string().trim().min(1).optional()
});
var WORD = /[\p{L}\p{N}_]/u;
var isWordChar = (c) => !!c && WORD.test(c);
var firstName = (name) => name.trim().split(/\s+/)[0] ?? "";
function findMentions(text3, members) {
  if (!text3.includes("@") || !members.length) return [];
  const full = members.map((m) => ({ m, key: m.name.trim().toLowerCase() })).filter((x) => x.key);
  const firstCounts = /* @__PURE__ */ new Map();
  for (const m of members) {
    const f = firstName(m.name).toLowerCase();
    if (f) firstCounts.set(f, (firstCounts.get(f) ?? 0) + 1);
  }
  const candidates = [
    ...full,
    ...members.map((m) => ({ m, key: firstName(m.name).toLowerCase() })).filter((x) => x.key && firstCounts.get(x.key) === 1 && x.key !== x.m.name.trim().toLowerCase())
  ].sort((a, b) => b.key.length - a.key.length);
  const spans = [];
  const lower = text3.toLowerCase();
  for (let i = 0; i < text3.length; i++) {
    if (text3[i] !== "@") continue;
    const prev = text3[i - 1];
    if (isWordChar(prev) || prev === "@" || prev === ".") continue;
    for (const c of candidates) {
      const end = i + 1 + c.key.length;
      if (lower.slice(i + 1, end) !== c.key) continue;
      if (isWordChar(text3[end])) continue;
      spans.push({ userId: c.m.id, name: c.m.name, start: i, end });
      i = end - 1;
      break;
    }
  }
  return spans;
}
var mentionedUserIds = (spans) => [...new Set(spans.map((s) => s.userId))];
function mentionParts(text3, spans) {
  const parts = [];
  let at = 0;
  for (const s of [...spans].sort((a, b) => a.start - b.start)) {
    if (s.start < at || s.end > text3.length) continue;
    if (s.start > at) parts.push({ text: text3.slice(at, s.start) });
    parts.push({ text: text3.slice(s.start, s.end), mention: { userId: s.userId, name: s.name } });
    at = s.end;
  }
  if (at < text3.length) parts.push({ text: text3.slice(at) });
  return parts;
}
function activeMentionQuery(text3, caret) {
  const before = text3.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (isWordChar(before[at - 1])) return null;
  const query = before.slice(at + 1);
  if (query.length > 40 || /[\n@]/.test(query) || /\s{2,}/.test(query) || /^\s/.test(query)) return null;
  return { start: at, query };
}
function suggestMembers(members, query, limit = 6) {
  const q = query.trim().toLowerCase();
  const scored = members.map((m) => {
    const n = m.name.toLowerCase();
    const score = !q ? 1 : n.startsWith(q) ? 3 : n.split(/\s+/).some((w) => w.startsWith(q)) ? 2 : 0;
    return { m, score };
  }).filter((x) => x.score > 0).sort((a, b) => b.score - a.score || a.m.name.localeCompare(b.m.name));
  return scored.slice(0, limit).map((x) => x.m);
}

// src/enrolment.ts
import { IANAZone as IANAZone3 } from "luxon";
import { z as z7 } from "zod";
var APPLICATION_STATUSES = ["SUBMITTED", "UNDER_REVIEW", "MORE_INFO", "APPROVED", "REJECTED"];
var OPEN_APPLICATION_STATUSES = ["SUBMITTED", "UNDER_REVIEW", "MORE_INFO"];
var APPLICATION_STATUS_LABEL = {
  SUBMITTED: "Submitted",
  UNDER_REVIEW: "Under review",
  MORE_INFO: "More info needed",
  APPROVED: "Approved",
  REJECTED: "Not approved"
};
var POLICY_KINDS = ["FLOOR_RULES", "CONFIDENTIALITY", "DISCLAIMER"];
var POLICY_KIND_LABEL = {
  FLOOR_RULES: "Floor rules",
  CONFIDENTIALITY: "Confidentiality terms",
  DISCLAIMER: "General-advice disclaimer"
};
var PASSWORD_MIN_LENGTH = 12;
var PASSWORD_MAX_LENGTH = 128;
var ENROLMENT_LIMITS = {
  name: 120,
  email: 254,
  phone: 32,
  country: 80,
  timezone: 64,
  markets: 10,
  symbols: 30,
  symbol: 20,
  brokers: 500,
  statement: 2e3,
  referral: 200,
  reply: 2e3,
  reason: 2e3,
  policyTitle: 160,
  policyBody: 5e4
};
var L = ENROLMENT_LIMITS;
var text = (max) => z7.string().trim().max(max, `Keep this under ${max} characters`);
var optText = (max) => text(max).nullish().transform((v) => v ? v : null);
var id = z7.string().trim().min(1).max(40);
var emailField = z7.string().trim().toLowerCase().max(L.email).email("Enter a valid email address");
var ianaTimezone = z7.string().trim().max(L.timezone).refine((v) => IANAZone3.isValidZone(v), "Pick a timezone from the list (e.g. Asia/Karachi)");
var marketCode = z7.string().trim().toUpperCase().regex(/^[A-Z0-9-]{2,12}$/, "Unknown market");
var symbolCode = z7.string().trim().toUpperCase().min(1).max(L.symbol, `Symbols are at most ${L.symbol} characters`).regex(/^[A-Z0-9][A-Z0-9._/-]*$/, "Use letters, digits, dot, dash or slash");
function parseSymbolList(input) {
  const out = [];
  for (const raw of input.split(/[\s,;]+/)) {
    const s = raw.trim().toUpperCase();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}
var applicationFieldsSchema = z7.object({
  name: text(L.name).min(2, "Enter your full name"),
  email: emailField,
  phone: z7.string().trim().max(L.phone).regex(/^\+?[0-9 ()-]{6,32}$/, "Enter a mobile number with country code, e.g. +92 300 1234567"),
  country: text(L.country).min(2, "Enter your country"),
  timezone: ianaTimezone,
  experienceYears: z7.number({ error: "Enter a number of years" }).int("Whole years only").min(0).max(60),
  marketsOfInterest: z7.array(marketCode).min(1, "Pick at least one market").max(L.markets),
  preferredSymbols: z7.array(symbolCode).max(L.symbols, `At most ${L.symbols} symbols`),
  brokers: optText(L.brokers),
  statement: text(L.statement).min(20, "Tell us a little more (at least 20 characters)"),
  referral: optText(L.referral)
});
var applicationSubmitSchema = applicationFieldsSchema.extend({
  preferredSymbols: applicationFieldsSchema.shape.preferredSymbols.default([]),
  acceptTerms: z7.literal(true, { error: "Please read and accept the floor rules, confidentiality terms and disclaimer" }),
  acceptedPolicyIds: z7.array(id).max(10).default([]),
  inviteToken: z7.string().trim().max(100).optional(),
  /** honeypot — hidden from people; bots fill it in */
  website: z7.string().max(200).optional()
});
var applicationUpdateSchema = applicationFieldsSchema.omit({ email: true }).partial().extend({ reply: text(L.reply).min(2, "Answer the question or say what you changed") });
var statusLinkRequestSchema = z7.object({ email: emailField, website: z7.string().max(200).optional() });
var applicationDecisionSchema = z7.discriminatedUnion("decision", [
  z7.object({
    decision: z7.literal("APPROVE"),
    roles: z7.array(z7.enum(ROLES)).min(1, "Pick at least one role").max(ROLES.length),
    accountIds: z7.array(id).max(10).default([]),
    newAccounts: z7.array(accountCreateSchema.omit({ riskPolicyId: true })).max(5).default([]),
    instrumentIds: z7.array(id).max(200).default([]),
    note: optText(L.reason)
  }),
  z7.object({ decision: z7.literal("REJECT"), reason: text(L.reason).min(3, "Give a reason \u2014 the applicant will see it") }),
  z7.object({ decision: z7.literal("MORE_INFO"), message: text(L.reason).min(3, "Tell the applicant what you need") })
]).superRefine((v, ctx) => {
  if (v.decision !== "APPROVE" || !v.roles.includes("TRADER")) return;
  if (v.accountIds.length + v.newAccounts.length === 0) ctx.addIssue({ code: "custom", path: ["accountIds"], message: "Give a trader at least one account (pick one or create one)" });
  if (v.instrumentIds.length === 0) ctx.addIssue({ code: "custom", path: ["instrumentIds"], message: "Give a trader at least one symbol to analyse" });
});
var inviteCreateSchema = z7.object({
  email: z7.preprocess((v) => typeof v === "string" && v.trim() === "" ? void 0 : v, emailField.optional()),
  name: optText(L.name),
  marketsOfInterest: z7.array(marketCode).max(L.markets).default([]),
  note: optText(500)
});
var newPasswordField = z7.string().min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`).max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters`);
var activationSchema = z7.object({
  password: newPasswordField,
  timezone: ianaTimezone
});
var optionalTotp = z7.preprocess(
  (v) => typeof v === "string" && v.trim() === "" ? void 0 : v,
  z7.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app").optional()
);
var passwordForgotSchema = z7.object({ email: emailField, website: z7.string().max(200).optional() });
var passwordResetSchema = z7.object({ password: newPasswordField, code: optionalTotp });
var passwordChangeSchema = z7.object({ currentPassword: z7.string().min(1, "Enter your current password").max(PASSWORD_MAX_LENGTH), newPassword: newPasswordField, code: optionalTotp }).refine((v) => v.newPassword !== v.currentPassword, { message: "Choose a password that is different from the current one", path: ["newPassword"] });
var policyPublishSchema = z7.object({
  kind: z7.enum(POLICY_KINDS),
  title: text(L.policyTitle).min(3, "Give it a title"),
  body: z7.string().trim().min(20, "The text is too short").max(L.policyBody, `Keep this under ${L.policyBody} characters`)
});
var policyAcceptSchema = z7.object({ policyDocumentIds: z7.array(id).min(1).max(10) });
var memberStatusSchema = z7.object({ status: z7.enum(["ACTIVE", "SUSPENDED"]), reason: optText(500) }).refine((v) => v.status !== "SUSPENDED" || (v.reason?.length ?? 0) >= 3, { message: "Give a reason for the suspension", path: ["reason"] });
var symbolRequestSchema = z7.object({ instrumentId: id.optional(), symbolText: symbolCode.optional(), reason: text(1e3).min(5, "Tell the reviewer why you want to cover it") }).refine((v) => !!v.instrumentId !== !!v.symbolText, { message: "Pick a symbol from the list, or type one that isn\u2019t listed", path: ["instrumentId"] });
var symbolRequestDecisionSchema = z7.object({ decision: z7.enum(["APPROVE", "REJECT"]), note: optText(1e3), instrumentId: id.optional() }).refine((v) => v.decision === "APPROVE" || (v.note?.length ?? 0) >= 3, { message: "Give a reason \u2014 the trader will see it", path: ["note"] });
function applicationQueueOrder(a, b) {
  if (a.invited !== b.invited) return a.invited ? -1 : 1;
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}
function pendingPolicies(current, acceptedDocumentIds) {
  const accepted = new Set(acceptedDocumentIds);
  return current.filter((d) => !accepted.has(d.id));
}
var DRAFT_POLICY_TEXTS = {
  FLOOR_RULES: {
    title: "Floor rules",
    body: [
      "These rules keep the floor accountable and safe for everyone. Breaking them can lead to suspension.",
      "",
      "1. Plan every trade in VTF before you place it. Every plan goes through the automatic risk check, and escalated plans wait for a Risk Manager.",
      "2. Stay inside the risk limits of your account. Never widen a stop loss without recording it \u2014 the Risk Manager is alerted.",
      '3. Publish your daily analysis for your assigned symbols before the deadline, or mark "no view" with a reason.',
      "4. Report on every open trade at the set interval until it closes, then file a closing report.",
      "5. Hand over your open trades before quiet hours or time away, so someone covers your reports.",
      "6. Keep discussion professional: debate ideas, not people. No harassment, spam or promotion of outside services.",
      "7. VTF never places orders. You are responsible for every order you place with your broker.",
      "8. Tell the floor admin straight away if you think your account or sign-in has been compromised."
    ].join("\n")
  },
  CONFIDENTIALITY: {
    title: "Confidentiality terms",
    body: [
      "Everything on the floor is confidential to floor members: analyses, setups, trade plans, trades, reports, comments, desk rooms, webinars, recordings, library material and details about other members.",
      "",
      "- Don\u2019t share, copy, screenshot or forward floor content outside VTF unless a Master Trader has published it publicly.",
      "- Don\u2019t share your password or authenticator codes. Each account is for one person only.",
      "- Don\u2019t record desk rooms or webinars yourself.",
      "- Keep other members\u2019 personal information private.",
      "- These obligations continue after you leave the floor.",
      "",
      "If you are not sure whether something can be shared, ask the floor admin first."
    ].join("\n")
  },
  DISCLAIMER: {
    title: "General advice warning",
    body: [
      "This content is provided by K5C Holdings Pty Ltd (ABN 80 624 329 559), trading as K5 Trading, for education and information only. It is general in nature and does not take into account your objectives, financial situation or needs, and it is not a recommendation to buy or sell any financial product.",
      "",
      "Trading forex, CFDs, futures, commodities and shares involves significant risk, and you may lose money. Past performance is not a reliable indicator of future results. Trades shown may be on demonstration or personal accounts.",
      "",
      "Consider seeking independent professional advice before making any financial decision."
    ].join("\n")
  }
};

// src/setups.ts
import { DateTime as DateTime5 } from "luxon";
import { z as z8 } from "zod";
var SETUP_STATUSES = ["WATCHING", "GETTING_READY", "TRIGGERED", "CONVERTED", "INVALIDATED", "EXPIRED"];
var SETUP_ACTIVE_STATUSES = ["WATCHING", "GETTING_READY", "TRIGGERED"];
var SETUP_LINKABLE_STATUSES = [...SETUP_ACTIVE_STATUSES, "CONVERTED"];
var SETUP_UPDATE_STATUSES = ["WATCHING", "GETTING_READY", "TRIGGERED", "INVALIDATED"];
var isSetupActive = (status) => SETUP_ACTIVE_STATUSES.includes(status);
var SETUP_STATUS_LABEL = {
  WATCHING: "Watching",
  GETTING_READY: "Getting ready",
  TRIGGERED: "Triggered",
  CONVERTED: "Converted to plan",
  INVALIDATED: "Invalidated",
  EXPIRED: "Expired"
};
function canMoveSetup(from, to) {
  return isSetupActive(from) && SETUP_UPDATE_STATUSES.includes(to);
}
var SETUP_FREQUENCIES = [
  { key: "15m", label: "Every 15 min", cadence: "INTERVAL", minutes: 15 },
  { key: "30m", label: "Every 30 min", cadence: "INTERVAL", minutes: 30 },
  { key: "1h", label: "Every hour", cadence: "INTERVAL", minutes: 60 },
  { key: "2h", label: "Every 2 hours", cadence: "INTERVAL", minutes: 120 },
  { key: "4h", label: "Every 4 hours", cadence: "INTERVAL", minutes: 240 },
  { key: "daily", label: "Daily", cadence: "DAILY", minutes: null },
  { key: "session_open", label: "At session open", cadence: "SESSION_OPEN", minutes: null },
  { key: "session_close", label: "At session close", cadence: "SESSION_CLOSE", minutes: null },
  { key: "custom", label: "Custom (minutes)", cadence: "INTERVAL", minutes: null }
];
function frequencyKey(cadence, minutes) {
  return SETUP_FREQUENCIES.find((f) => f.cadence === cadence && (cadence !== "INTERVAL" || f.minutes === minutes))?.key ?? "custom";
}
function cadenceLabel(cadence, minutes) {
  const preset = SETUP_FREQUENCIES.find((f) => f.key !== "custom" && f.cadence === cadence && (cadence !== "INTERVAL" || f.minutes === minutes));
  if (preset) return preset.label;
  if (cadence === "INTERVAL" && minutes) return minutes % 60 === 0 ? `Every ${minutes / 60} hours` : `Every ${minutes} min`;
  return "Custom";
}
function nextSetupUpdateDue(a) {
  const from = typeof a.from === "string" ? new Date(a.from) : a.from;
  let due;
  switch (a.cadence) {
    case "INTERVAL":
      due = new Date(from.getTime() + Math.max(1, a.minutes ?? 60) * 6e4);
      break;
    case "DAILY":
      due = new Date(from.getTime() + 24 * 36e5);
      break;
    case "SESSION_OPEN":
      due = nextOpenAfterClose(a.market, from);
      break;
    case "SESSION_CLOSE":
      due = nextSessionClose(a.market, from);
      break;
  }
  if (!due) return null;
  const covered = (at) => typeof a.covered === "function" ? a.covered(at) : !!a.covered;
  for (let i = 0; i < 8; i++) {
    let moved = false;
    if ((a.cadence === "INTERVAL" || a.cadence === "DAILY") && !isInSession(a.market, due)) {
      const open = nextSessionOpen(a.market, due);
      if (open) {
        due = open;
        moved = true;
      }
    }
    if (a.quietHours && !covered(due) && inQuietHours(a.quietHours, due)) {
      due = quietHoursEnd(a.quietHours, due);
      moved = true;
    }
    if (!moved) break;
  }
  return due;
}
var tradingWeek = (d, zone) => {
  const t = DateTime5.fromJSDate(d, { zone }).plus({ days: 1 });
  return t.weekYear * 100 + t.weekNumber;
};
function defaultSetupExpiry(market, now, rule) {
  const fallback = new Date(now.getTime() + 7 * 864e5);
  if (typeof rule === "number") return new Date(now.getTime() + rule * 6e4);
  if (rule === "end_of_day") return nextSessionClose(market, now) ?? fallback;
  let cursor = now;
  for (let i = 0; i < 40; i++) {
    const close = nextSessionClose(market, cursor);
    if (!close) break;
    const open = nextOpenAfterClose(market, close);
    if (!open || tradingWeek(open, market.timezone) !== tradingWeek(close, market.timezone)) return close;
    cursor = close;
  }
  return fallback;
}
function setupLevelsIssue(s) {
  const low = dec(s.zoneLow);
  const high = s.zoneHigh ? dec(s.zoneHigh) : low;
  if (high.lt(low)) return "The zone's upper price is below its lower price.";
  if (!s.invalidationPrice) return null;
  const inv = dec(s.invalidationPrice);
  if (s.direction === "LONG" && inv.gte(low)) return "For a long setup the invalidation level should be below the zone.";
  if (s.direction === "SHORT" && inv.lte(high)) return "For a short setup the invalidation level should be above the zone.";
  return null;
}
function setupPlanDraft(s) {
  const entry = s.direction === "LONG" ? s.zoneHigh ?? s.zoneLow : s.zoneLow;
  const e = dec(entry);
  const targets = (s.targetIdea ?? "").match(/\d[\d,]*(?:\.\d+)?/g)?.map((n) => n.replace(/,/g, "")).filter((n) => {
    const p = dec(n);
    return s.direction === "LONG" ? p.gt(e) : p.lt(e) && p.gt(0);
  }).slice(0, 2) ?? [];
  const rationale = [`Setup trigger: ${s.triggerCondition}`, s.targetIdea ? `Target idea: ${s.targetIdea}` : null].filter(Boolean).join("\n");
  return {
    direction: s.direction,
    orderType: "LIMIT",
    plannedEntry: dec(entry).toString(),
    stopLoss: s.invalidationPrice ? dec(s.invalidationPrice).toString() : null,
    takeProfits: targets.map((price) => ({ price, sizePct: targets.length === 2 ? 50 : 100 })),
    timeframe: s.timeframe ?? null,
    rationale
  };
}
var optionalLink = z8.string().trim().max(1024).refine((v) => v === "" || /^https?:\/\/\S+$/i.test(v), "Use a link that starts with http:// or https://").transform((v) => v === "" ? null : v).nullable().optional();
var optionalPrice = z8.preprocess((v) => v === "" ? null : v, positiveDecimal.nullable().optional());
var setupFields = z8.object({
  instrumentId: z8.string().min(1),
  direction: z8.enum(["LONG", "SHORT"]),
  timeframe: z8.string().trim().min(1, "Pick a timeframe").max(10),
  strategyId: z8.string().min(1).nullable().optional(),
  zoneLow: positiveDecimal,
  zoneHigh: optionalPrice,
  triggerCondition: z8.string().trim().min(3, "Describe what would trigger it").max(2e3),
  invalidationPrice: positiveDecimal,
  targetIdea: z8.string().trim().max(2e3).nullable().optional(),
  confidence: z8.enum(["LOW", "MEDIUM", "HIGH"]).nullable().optional(),
  cadence: z8.enum(["INTERVAL", "DAILY", "SESSION_OPEN", "SESSION_CLOSE"]),
  cadenceMinutes: z8.number().int().min(5, "At least 5 minutes").max(7 * 24 * 60).nullable().optional(),
  /** null/absent = SETUP_DEFAULT_EXPIRY */
  expiresAt: z8.coerce.date().nullable().optional(),
  chartUrl: optionalLink,
  /** the TradingView signal this setup is made from ("Make a setup from this", INT-SYN-02); fixed once created */
  signalId: z8.string().min(1).max(40).nullable().optional()
});
var cadenceIssue = (v) => v.cadence === "INTERVAL" && !v.cadenceMinutes;
var setupCreateSchema = setupFields.refine((v) => !cadenceIssue(v), { message: "Give the interval in minutes", path: ["cadenceMinutes"] }).superRefine((v, ctx) => {
  const issue = setupLevelsIssue(v);
  if (issue) ctx.addIssue({ code: "custom", message: issue, path: ["invalidationPrice"] });
});
var setupEditSchema = setupFields.omit({ instrumentId: true, direction: true, signalId: true }).partial().refine((v) => !cadenceIssue(v), { message: "Give the interval in minutes", path: ["cadenceMinutes"] });
var setupPostUpdateSchema = z8.object({
  status: z8.enum(SETUP_UPDATE_STATUSES),
  whatChanged: z8.string().trim().min(3, "Say what changed").max(5e3),
  distanceNote: z8.string().trim().max(2e3).nullable().optional(),
  screenshotUrl: optionalLink,
  confidence: z8.enum(["LOW", "MEDIUM", "HIGH"]).nullable().optional()
});

// src/handover.ts
import { z as z9 } from "zod";
var HANDOVER_RIGHTS = ["REPORT_ONLY", "MANAGE"];
var HANDOVER_RIGHTS_LABEL = {
  REPORT_ONLY: "Report only",
  MANAGE: "Manage (record trade events)"
};
var HANDOVER_STATUS_LABEL = {
  REQUESTED: "Waiting for an answer",
  ACCEPTED: "Accepted \xB7 starts later",
  DECLINED: "Declined",
  NOT_ACCEPTED: "Not accepted in time",
  ACTIVE: "Active",
  ENDED: "Ended",
  CANCELLED: "Cancelled"
};
var HANDOVER_LIVE = ["REQUESTED", "ACCEPTED", "ACTIVE"];
function quietHoursOf(u) {
  return u.quietHoursEnabled && u.quietHoursStart && u.quietHoursEnd ? { start: u.quietHoursStart, end: u.quietHoursEnd, tz: u.timezone } : null;
}
function handoverCoversAt(h, ownerQuiet, at) {
  if (h.status !== "ACCEPTED" && h.status !== "ACTIVE") return false;
  if (at.getTime() < h.startsAt.getTime() || at.getTime() >= h.endsAt.getTime()) return false;
  if (!h.recurringQuietHours) return true;
  return !!ownerQuiet && inQuietHours(ownerQuiet, at);
}
var emptyHandoverSummary = () => ({ coveringSince: null, windows: [] });
function readHandoverSummary(v) {
  const o = v && typeof v === "object" ? v : {};
  return { coveringSince: typeof o.coveringSince === "string" ? o.coveringSince : null, windows: Array.isArray(o.windows) ? o.windows : [] };
}
function totalHandoverSummary(windows) {
  return windows.reduce(
    (t, w) => ({ progressReports: t.progressReports + w.progressReports, tradeEvents: t.tradeEvents + w.tradeEvents, setupUpdates: t.setupUpdates + w.setupUpdates, missed: t.missed + w.missed, closedTrades: t.closedTrades + w.closedTrades }),
    { progressReports: 0, tradeEvents: 0, setupUpdates: 0, missed: 0, closedTrades: 0 }
  );
}
var plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
function handoverSummaryText(coverName, t) {
  const did = [
    t.progressReports && plural(t.progressReports, "progress report"),
    t.tradeEvents && plural(t.tradeEvents, "trade event"),
    t.setupUpdates && plural(t.setupUpdates, "setup update")
  ].filter(Boolean);
  const parts = [did.length ? `${coverName} posted ${did.length > 1 ? `${did.slice(0, -1).join(", ")} and ${did.at(-1)}` : did[0]}.` : `${coverName} didn't need to post anything.`];
  if (t.closedTrades) parts.push(`${plural(t.closedTrades, "trade was", "trades were")} closed.`);
  parts.push(t.missed ? `${plural(t.missed, "report or update was", "reports or updates were")} missed.` : "Nothing was missed.");
  return parts.join(" ");
}
var handoverCreateSchema = z9.object({
  coverId: z9.string().min(1, "Pick who covers"),
  tradeIds: z9.array(z9.string().min(1)).max(50).default([]),
  setupIds: z9.array(z9.string().min(1)).max(50).default([]),
  mode: z9.enum(["WINDOW", "QUIET_HOURS"]),
  startsAt: z9.coerce.date().nullable().optional(),
  endsAt: z9.coerce.date(),
  rights: z9.enum(HANDOVER_RIGHTS).default("REPORT_ONLY"),
  notes: z9.string().trim().max(5e3).nullable().optional()
}).refine((v) => v.tradeIds.length + v.setupIds.length > 0, { message: "Pick at least one trade or setup", path: ["tradeIds"] }).refine((v) => !v.startsAt || v.endsAt.getTime() > v.startsAt.getTime(), { message: "The end must be after the start", path: ["endsAt"] });

// src/admin.ts
import { DateTime as DateTime6 } from "luxon";
import { z as z10 } from "zod";
var isoDate2 = z10.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
var SCOPES = ["GLOBAL", "MARKET", "ACCOUNT", "TRADE", "SETUP", "USER"];
var SETTING_EDITORS = {
  PROGRESS_REPORT_INTERVAL: ["ADMIN", "MASTER_TRADER"]
};
var TRADER_SHORTER_ON_OWN_TRADE = ["PROGRESS_REPORT_INTERVAL"];
var PROGRESS_SCHEDULE_KEYS = ["PROGRESS_REPORT_INTERVAL", "PROGRESS_REPORT_ANCHOR", "PROGRESS_REPORT_GRACE", "PROGRESS_REPORT_MISS_AFTER", "PROGRESS_REPORT_SESSION_ONLY"];
var isSettingKey = (key) => Object.prototype.hasOwnProperty.call(SETTINGS, key);
function settingEditors(key) {
  return isSettingKey(key) && SETTING_EDITORS[key] || ["ADMIN"];
}
function canEditSetting(roles, key) {
  const editors = settingEditors(key);
  return !!roles?.some((r) => editors.includes(r));
}
var SCOPE_LABEL = { GLOBAL: "Whole floor", MARKET: "Market", ACCOUNT: "Account", TRADE: "Trade", SETUP: "Setup", USER: "Person" };
var ROLE_LABEL = { ADMIN: "Admin", MASTER_TRADER: "Master Trader", RISK_MANAGER: "Risk Manager", TRADER: "Trader" };
var UNITS = {
  PROGRESS_REPORT_INTERVAL: "minutes",
  PROGRESS_REPORT_GRACE: "minutes",
  PROGRESS_REPORT_MISS_AFTER: "minutes",
  APPROVAL_SLA: "minutes",
  APPROVAL_VALIDITY: "minutes",
  CLOSING_REPORT_WINDOW: "minutes",
  DAILY_LOSS_WARNING_PCT: "percent",
  FX_RATE_MAX_AGE_DAYS: "days",
  AUDIT_EXPORT_MAX_ROWS: "rows",
  STRATEGY_STATS_MIN_TRADES: "trades",
  SIGNAL_DEDUPE_MINUTES: "minutes",
  SIGNAL_WEBHOOK_RATE_LIMIT: "per_minute",
  SIGNAL_FLASH_BARS: "bars",
  PRICE_POLL_SECONDS: "seconds",
  PRICE_STALE_MINUTES: "minutes"
};
var SETTING_GROUPS = [
  { title: "Progress reports", keys: ["PROGRESS_REPORT_INTERVAL", "PROGRESS_REPORT_ANCHOR", "PROGRESS_REPORT_GRACE", "PROGRESS_REPORT_MISS_AFTER", "PROGRESS_REPORT_SESSION_ONLY", "CLOSING_REPORT_WINDOW"] },
  { title: "Analysis and approvals", keys: ["ANALYSIS_REQUIRES_REVIEW", "APPROVAL_SLA", "APPROVAL_VALIDITY", "DAILY_LOSS_WARNING_PCT"] },
  { title: "Currencies and FX", keys: ["REPORTING_CURRENCIES", "FX_RATE_SOURCE", "FX_RATE_MAX_AGE_DAYS"] },
  { title: "Floor and records", keys: ["FLOOR_SEES_MONEY_PNL", "AUDIT_EXPORT_MAX_ROWS"] },
  { title: "Analytics", keys: ["STRATEGY_STATS_MIN_TRADES"] },
  { title: "Live and streaming", keys: ["WEBINAR_REMINDERS", "WEBINAR_CAPACITY", "BRAND_NAME", "BRAND_OPERATOR", "STREAM_SHORT_DISCLAIMER", "STREAM_RETRY_LIMIT", "RECORDING_PUBLISH_APPROVER", "RECORDING_DEFAULT_PRIVACY", "RECORDING_YOUTUBE_PLAYLIST", "RECORDING_PUBLISH_RETRIES"] },
  { title: "Signals and prices", keys: ["SIGNAL_DEDUPE_MINUTES", "SIGNAL_WEBHOOK_RATE_LIMIT", "SIGNAL_FLASH_BARS", "PRICE_SOURCE", "PRICE_POLL_SECONDS", "PRICE_STALE_MINUTES"] }
];
function describeSetting(key) {
  const d = SETTINGS[key];
  const s = d.schema;
  const type = s.def?.type;
  const kind = type === "number" ? "int" : type === "boolean" ? "bool" : type === "enum" ? "enum" : type === "array" ? "list" : "other";
  return {
    key,
    description: d.description,
    default: d.default,
    scopes: [...d.scopes],
    editors: settingEditors(key),
    kind,
    ...kind === "int" && s.minValue != null ? { min: s.minValue } : {},
    ...kind === "int" && s.maxValue != null ? { max: s.maxValue } : {},
    ...kind === "enum" && s.options ? { options: [...s.options] } : {},
    ...UNITS[key] ? { unit: UNITS[key] } : {},
    group: SETTING_GROUPS.find((g) => g.keys.includes(key))?.title ?? "Other"
  };
}
function describeSettings() {
  const order = SETTING_GROUPS.flatMap((g) => g.keys);
  const rank = (k) => order.includes(k) ? order.indexOf(k) : order.length;
  return Object.keys(SETTINGS).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).map(describeSetting);
}
function formatSettingValue(key, value) {
  if (value === void 0 || value === null) return "\u2014";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (Array.isArray(value)) return value.join(", ");
  const unit = isSettingKey(key) ? UNITS[key] : void 0;
  if (typeof value === "number" && unit === "minutes") {
    if (value >= 60 && value % 60 === 0) return `${value} min (${value / 60} h)`;
    if (value > 60) return `${value} min (${Math.floor(value / 60)} h ${value % 60} min)`;
    return `${value} min`;
  }
  if (typeof value === "number" && unit === "days") return `${value} day${value === 1 ? "" : "s"}`;
  if (typeof value === "number" && unit === "percent") return `${value} %`;
  if (typeof value === "number" && unit === "rows") return `${value.toLocaleString("en-GB")} rows`;
  if (typeof value === "number" && unit === "trades") return `${value} closed trade${value === 1 ? "" : "s"}`;
  if (typeof value === "number" && unit === "seconds") return value >= 60 && value % 60 === 0 ? `${value} s (${value / 60} min)` : `${value} s`;
  if (typeof value === "number" && unit === "per_minute") return `${value} per minute`;
  if (typeof value === "number" && unit === "bars") return `${value} bar${value === 1 ? "" : "s"}`;
  return String(value);
}
function validateSettingValue(key, value) {
  if (!isSettingKey(key)) return { ok: false, message: `There is no setting called ${key}.` };
  const d = describeSetting(key);
  let v = value;
  if (d.kind === "list" && Array.isArray(v)) v = [...new Set(v.map((x) => typeof x === "string" ? x.trim().toUpperCase() : x))];
  const parsed = SETTINGS[key].schema.safeParse(v);
  if (parsed.success) return { ok: true, value: parsed.data };
  const unit = d.unit === "minutes" ? " minutes" : d.unit === "days" ? " days" : d.unit === "percent" ? " %" : d.unit === "trades" ? " trades" : d.unit === "seconds" ? " seconds" : d.unit === "per_minute" ? " per minute" : "";
  switch (d.kind) {
    case "int":
      return { ok: false, message: `Enter a whole number from ${d.min ?? "\u2026"} to ${d.max ?? "\u2026"}${unit}${key === "PROGRESS_REPORT_INTERVAL" ? " (30 min to 24 h)" : ""}.` };
    case "bool":
      return { ok: false, message: "Choose on or off." };
    case "enum":
      return { ok: false, message: `Choose one of: ${d.options?.join(", ")}.` };
    case "list":
      return { ok: false, message: "Enter one or more three-letter currency codes, e.g. USD, AUD, PKR." };
    default:
      return { ok: false, message: `That value isn't valid for ${key}.` };
  }
}
var settingChangeSchema = z10.object({
  key: z10.string().trim().min(1).max(64),
  scope: z10.enum(SCOPES),
  scopeId: z10.string().trim().min(1).max(64).default("*"),
  value: z10.unknown()
});
var settingClearSchema = settingChangeSchema.omit({ value: true });
var effectiveQuerySchema = z10.object({
  key: z10.string().trim().max(64).optional(),
  marketId: z10.string().trim().max(64).optional(),
  accountId: z10.string().trim().max(64).optional(),
  tradeId: z10.string().trim().max(64).optional(),
  setupId: z10.string().trim().max(64).optional(),
  userId: z10.string().trim().max(64).optional()
});
function explainSetting(key, context, stored) {
  const d = SETTINGS[key];
  const chain = [];
  let used = null;
  let value = d.default;
  for (const scope of SCOPE_PRECEDENCE) {
    if (!d.scopes.includes(scope)) continue;
    const scopeId = scope === "GLOBAL" ? "*" : context[scope];
    if (scopeId === void 0) {
      chain.push({ scope, scopeId: null, value: void 0, status: "no_target" });
      continue;
    }
    const hit = stored.find((s) => s.key === key && s.scope === scope && s.scopeId === scopeId);
    if (!hit) {
      chain.push({ scope, scopeId, value: void 0, status: "not_set" });
      continue;
    }
    const parsed = d.schema.safeParse(hit.value);
    if (!parsed.success) chain.push({ scope, scopeId, value: hit.value, status: "invalid" });
    else if (used) chain.push({ scope, scopeId, value: parsed.data, status: "shadowed" });
    else {
      used = { scope, scopeId };
      value = parsed.data;
      chain.push({ scope, scopeId, value: parsed.data, status: "used" });
    }
  }
  chain.push({ scope: "DEFAULT", scopeId: null, value: d.default, status: used ? "shadowed" : "used" });
  return { key, value, source: used, chain };
}
var pct2 = (max) => decimalString.refine((v) => Number(v) > 0 && Number(v) <= max, `Enter a percentage above 0 and up to ${max}`);
var riskPolicyUpdateSchema = z10.object({
  autoMaxRiskPct: pct2(10),
  hardMaxRiskPct: pct2(20),
  maxOpenRiskPct: pct2(50),
  maxOpenTrades: z10.number().int().min(1).max(100),
  dailyLossLimitPct: pct2(50),
  minRewardRisk: decimalString.refine((v) => Number(v) >= 0 && Number(v) <= 20, "Enter a ratio from 0 to 20"),
  requireStopLoss: z10.boolean(),
  requireLinkedAnalysis: z10.boolean()
}).partial().refine((o) => Object.keys(o).length > 0, "Change at least one value");
var roleChangeSchema = z10.object({ roles: z10.array(z10.enum(ROLES)).min(1, "Keep at least one role").max(ROLES.length) });
var COMPLIANCE_PERIODS = ["this_week", "last_week", "this_month", "custom"];
var periodRules = (schema) => schema.refine((q) => q.period !== "custom" || !!(q.from && q.to), { message: "Pick a start and an end date", path: ["from"] }).refine((q) => q.period !== "custom" || !q.from || !q.to || q.from <= q.to, { message: "The start date is after the end date", path: ["to"] }).refine((q) => q.period !== "custom" || !q.from || !q.to || DateTime6.fromISO(q.to).diff(DateTime6.fromISO(q.from), "days").days <= 366, { message: "Pick at most a year", path: ["to"] });
var periodFields = { period: z10.enum(COMPLIANCE_PERIODS).default("this_week"), from: isoDate2.optional(), to: isoDate2.optional() };
var complianceQuerySchema = periodRules(z10.object(periodFields));
var COMPLIANCE_METRICS = ["analyses", "progress", "closing", "stale_setups", "unplanned", "learning"];
var DUE_OUTCOMES = ["on_time", "late", "missed", "open"];
var complianceItemsQuerySchema = periodRules(
  z10.object({
    ...periodFields,
    traderId: z10.string().trim().min(1).max(64),
    metric: z10.enum(COMPLIANCE_METRICS),
    outcome: z10.enum(DUE_OUTCOMES).optional()
  })
);
function compliancePeriod(q, now, tz) {
  const local = DateTime6.fromJSDate(now, { zone: tz });
  let start;
  let end;
  let name;
  switch (q.period) {
    case "last_week":
      start = local.startOf("week").minus({ weeks: 1 });
      end = start.plus({ weeks: 1 });
      name = "Last week";
      break;
    case "this_month":
      start = local.startOf("month");
      end = start.plus({ months: 1 });
      name = "This month";
      break;
    case "custom":
      start = DateTime6.fromISO(q.from, { zone: tz }).startOf("day");
      end = DateTime6.fromISO(q.to, { zone: tz }).startOf("day").plus({ days: 1 });
      name = "Custom";
      break;
    default:
      start = local.startOf("week");
      end = start.plus({ weeks: 1 });
      name = "This week";
  }
  const last = end.minus({ days: 1 });
  const span = start.hasSame(last, "month") ? `${start.toFormat("d")}\u2013${last.toFormat("d LLL yyyy")}` : `${start.toFormat("d LLL")} \u2013 ${last.toFormat("d LLL yyyy")}`;
  return { from: start.toUTC().toJSDate(), to: end.toUTC().toJSDate(), fromDay: start.toISODate(), toDay: last.toISODate(), label: `${name} \xB7 ${span}` };
}
function dueOutcome(item) {
  switch (item.status) {
    case "DONE":
      return item.isLate ? "late" : "on_time";
    case "MISSED":
      return item.completedAt ? "late" : "missed";
    case "CANCELLED":
      return "cancelled";
    default:
      return "open";
  }
}
var emptyCounts = () => ({ onTime: 0, late: 0, missed: 0, open: 0 });
function addOutcome(c, o) {
  if (o === "on_time") c.onTime++;
  else if (o === "late") c.late++;
  else if (o === "missed") c.missed++;
  else if (o === "open") c.open++;
}
function complianceRate(parts) {
  const onTime = parts.reduce((s, p) => s + p.onTime, 0);
  const total = parts.reduce((s, p) => s + p.onTime + p.late + p.missed, 0);
  return total === 0 ? null : Math.round(onTime / total * 100);
}
function compareCompliance(a, b) {
  const ra = a.rate ?? 100;
  const rb = b.rate ?? 100;
  return ra - rb || b.missed - a.missed || b.issues - a.issues || Number(a.rate === null) - Number(b.rate === null) || a.name.localeCompare(b.name);
}
var auditQuerySchema = z10.object({
  from: isoDate2.optional(),
  to: isoDate2.optional(),
  actorId: z10.string().trim().max(64).optional(),
  action: z10.string().trim().max(120).optional(),
  entityType: z10.string().trim().max(120).optional(),
  entityId: z10.string().trim().max(191).optional(),
  q: z10.string().trim().max(200).optional(),
  cursor: z10.string().trim().max(200).optional(),
  limit: z10.coerce.number().int().min(1).max(200).default(50)
});
var isPlain = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
function jsonDiff(before, after, path = "") {
  if (JSON.stringify(before) === JSON.stringify(after)) return [];
  const bothObjects = isPlain(before) && isPlain(after);
  const bothArrays = Array.isArray(before) && Array.isArray(after);
  if (!bothObjects && !bothArrays) {
    if (before === void 0) return [{ path: path || "(value)", kind: "added", after }];
    if (after === void 0) return [{ path: path || "(value)", kind: "removed", before }];
    return [{ path: path || "(value)", kind: "changed", before, after }];
  }
  const out = [];
  if (bothArrays) {
    const a2 = before;
    const b2 = after;
    for (let i = 0; i < Math.max(a2.length, b2.length); i++) out.push(...jsonDiff(a2[i], b2[i], `${path}[${i}]`));
    return out;
  }
  const a = before;
  const b = after;
  for (const k of [.../* @__PURE__ */ new Set([...Object.keys(a), ...Object.keys(b)])].sort()) out.push(...jsonDiff(a[k], b[k], path ? `${path}.${k}` : k));
  return out;
}
function csvSafeCell(v) {
  let s = v === null || v === void 0 ? "" : typeof v === "string" ? v : v instanceof Date ? v.toISOString() : JSON.stringify(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
var FX_PROVIDER = { id: "open.er-api", name: "open.er-api.com", url: "https://open.er-api.com/v6/latest/USD", attribution: "Rates By Exchange Rate API", attributionUrl: "https://www.exchangerate-api.com" };
var reportingCurrencySchema = z10.object({ reportingCurrency: z10.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Use a three-letter currency code") });

// src/library.ts
import { z as z11 } from "zod";
var CONTENT_LEVELS = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];
var ONBOARDING_TAG = "onboarding";
var MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
var OOXML = "application/vnd.openxmlformats-officedocument";
var FILE_TYPES = [
  { ext: "pdf", mime: "application/pdf", kind: "pdf", magic: "pdf" },
  { ext: "png", mime: "image/png", kind: "image", magic: "png" },
  { ext: "jpg", mime: "image/jpeg", kind: "image", magic: "jpeg" },
  { ext: "jpeg", mime: "image/jpeg", kind: "image", magic: "jpeg" },
  { ext: "gif", mime: "image/gif", kind: "image", magic: "gif" },
  { ext: "webp", mime: "image/webp", kind: "image", magic: "webp" },
  { ext: "docx", mime: `${OOXML}.wordprocessingml.document`, kind: "word", magic: "zip" },
  { ext: "doc", mime: "application/msword", kind: "word", magic: "ole" },
  { ext: "xlsx", mime: `${OOXML}.spreadsheetml.sheet`, kind: "excel", magic: "zip" },
  { ext: "xls", mime: "application/vnd.ms-excel", kind: "excel", magic: "ole" },
  { ext: "pptx", mime: `${OOXML}.presentationml.presentation`, kind: "powerpoint", magic: "zip" },
  { ext: "ppt", mime: "application/vnd.ms-powerpoint", kind: "powerpoint", magic: "ole" },
  { ext: "txt", mime: "text/plain; charset=utf-8", kind: "text", magic: "text" },
  { ext: "csv", mime: "text/csv; charset=utf-8", kind: "text", magic: "text" },
  { ext: "md", mime: "text/markdown; charset=utf-8", kind: "text", magic: "text" }
];
var FILE_ACCEPT = FILE_TYPES.map((t) => `.${t.ext}`).join(",");
var fileExtension = (filename) => /\.([A-Za-z0-9]{1,8})$/.exec(filename.trim())?.[1]?.toLowerCase() ?? "";
var fileTypeFor = (filename) => FILE_TYPES.find((t) => t.ext === fileExtension(filename)) ?? null;
var fileTypeByMime = (mime) => FILE_TYPES.find((t) => t.mime === mime) ?? null;
var isInlineKind = (kind) => kind === "pdf" || kind === "image";
var startsWith = (b, sig, at = 0) => b.length >= at + sig.length && sig.every((v, i) => b[at + i] === v);
var ascii = (s) => [...s].map((c) => c.charCodeAt(0));
function matchesMagic(type, head) {
  switch (type.magic) {
    case "pdf": {
      const sig = ascii("%PDF-");
      for (let i = 0; i <= Math.min(1024, head.length - sig.length); i++) if (startsWith(head, sig, i)) return true;
      return false;
    }
    case "png":
      return startsWith(head, [137, 80, 78, 71, 13, 10, 26, 10]);
    case "jpeg":
      return startsWith(head, [255, 216, 255]);
    case "gif":
      return startsWith(head, ascii("GIF87a")) || startsWith(head, ascii("GIF89a"));
    case "webp":
      return startsWith(head, ascii("RIFF")) && startsWith(head, ascii("WEBP"), 8);
    case "zip":
      return startsWith(head, [80, 75, 3, 4]);
    case "ole":
      return startsWith(head, [208, 207, 17, 224, 161, 177, 26, 225]);
    case "text": {
      if (head.includes(0)) return false;
      try {
        new TextDecoder("utf-8", { fatal: true }).decode(head, { stream: true });
        return true;
      } catch {
        return false;
      }
    }
  }
}
function cleanFilename(name, fallbackExt = "bin") {
  const base = (name.split(/[\\/]/).pop() ?? "").replace(/[\u0000-\u001f\u007f"]/g, "").replace(/\s+/g, " ").trim();
  if (!base || base === "." || base === "..") return `file.${fallbackExt}`;
  if (base.length <= 200) return base;
  const ext = fileExtension(base);
  return `${base.slice(0, 190 - ext.length).trim()}${ext ? `.${ext}` : ""}`;
}
function videoEmbed(raw) {
  let u;
  try {
    u = new URL(raw.trim());
  } catch {
    return { provider: null, embedUrl: null };
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return { provider: null, embedUrl: null };
  const host = u.hostname.toLowerCase().replace(/^(www\.|m\.)/, "");
  const parts = u.pathname.split("/").filter(Boolean);
  const ytId = (s) => s && /^[A-Za-z0-9_-]{11}$/.test(s) ? s : null;
  let id5 = null;
  if (host === "youtu.be") id5 = ytId(parts[0]);
  else if (host === "youtube.com" || host === "youtube-nocookie.com" || host === "music.youtube.com") {
    if (parts[0] === "watch") id5 = ytId(u.searchParams.get("v") ?? void 0);
    else if (["embed", "shorts", "live", "v"].includes(parts[0] ?? "")) id5 = ytId(parts[1]);
  }
  if (id5) {
    const t = /^(\d+)s?$/.exec(u.searchParams.get("t") ?? u.searchParams.get("start") ?? "")?.[1];
    return { provider: "youtube", embedUrl: `https://www.youtube-nocookie.com/embed/${id5}${t ? `?start=${t}` : ""}` };
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const idx = parts.findIndex((p) => /^\d{5,12}$/.test(p));
    if (idx >= 0) {
      const vid = parts[idx];
      const hash = u.searchParams.get("h") ?? (parts[idx + 1] && /^[0-9a-f]{6,20}$/i.test(parts[idx + 1]) ? parts[idx + 1] : null);
      return { provider: "vimeo", embedUrl: `https://player.vimeo.com/video/${vid}${hash ? `?h=${hash}` : ""}` };
    }
  }
  return { provider: null, embedUrl: null };
}
var httpUrl = z11.string().trim().max(1024, "That link is too long").refine((v) => {
  try {
    const u = new URL(v);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}, "Enter a full link starting with https://");
var tag = z11.string().trim().toLowerCase().min(1).max(40, "Tags are at most 40 characters").regex(/^[\p{L}\p{N}][\p{L}\p{N} _&-]*$/u, "Tags use letters, numbers, spaces and dashes");
var tags = z11.array(tag).max(12, "At most 12 tags").default([]).transform((t) => [...new Set(t)]);
var id2 = z11.string().trim().min(1).max(64);
var audienceSchema = z11.object({
  floor: z11.boolean().default(false),
  marketIds: z11.array(id2).max(20).default([]),
  userIds: z11.array(id2).max(200).default([])
}).refine((a) => a.floor || a.marketIds.length > 0 || a.userIds.length > 0, { message: "Choose who can see this: the whole floor, one or more markets, or named traders." });
var itemFields = {
  title: z11.string().trim().min(2, "Give it a title").max(190),
  description: z11.string().trim().max(1e4).nullish(),
  collectionId: id2.nullish(),
  sortOrder: z11.coerce.number().int().min(0).max(9999).default(0),
  tags,
  level: z11.enum(CONTENT_LEVELS).nullish(),
  audience: audienceSchema
};
var libraryItemCreateSchema = z11.discriminatedUnion("type", [
  z11.object({ type: z11.literal("VIDEO_LINK"), url: httpUrl, ...itemFields, publish: z11.boolean().default(false) }),
  z11.object({ type: z11.literal("DOCUMENT"), fileId: id2, allowDownload: z11.boolean().default(true), watermark: z11.boolean().default(true), ...itemFields, publish: z11.boolean().default(false) })
]);
var libraryItemUpdateSchema = z11.object({
  url: httpUrl,
  fileId: id2,
  allowDownload: z11.boolean(),
  watermark: z11.boolean(),
  title: itemFields.title,
  description: itemFields.description,
  collectionId: itemFields.collectionId,
  sortOrder: z11.coerce.number().int().min(0).max(9999),
  tags,
  level: itemFields.level,
  audience: audienceSchema
}).partial();
var collectionSchema = z11.object({
  title: z11.string().trim().min(2, "Give the collection a name").max(190),
  description: z11.string().trim().max(5e3).nullish(),
  level: z11.enum(CONTENT_LEVELS).nullish(),
  sortOrder: z11.coerce.number().int().min(0).max(9999).default(0)
});
var longText = (label) => z11.string().trim().min(2, `Describe the ${label}`).max(2e4);
var checklistItemSchema = z11.object({
  id: z11.string().regex(/^[A-Za-z0-9_-]{1,24}$/).optional(),
  text: z11.string().trim().min(2, "Checklist items need some text").max(300),
  /** a must-have: shown more prominently to the Risk Manager when unticked */
  required: z11.boolean().default(false)
});
var strategyExampleSchema = z11.object({ caption: z11.string().trim().max(200).default(""), url: httpUrl.optional(), fileId: id2.optional() }).refine((e) => Boolean(e.url) !== Boolean(e.fileId), "Each example is either a link or an uploaded image");
var strategyContentSchema = z11.object({
  markets: z11.array(z11.string().trim().toUpperCase().min(1).max(10)).max(10).default([]),
  timeframes: z11.array(z11.string().trim().min(1).max(10)).max(10).default([]),
  concept: longText("concept"),
  setupConditions: longText("setup conditions"),
  entryTrigger: longText("entry trigger"),
  stopLossRule: longText("stop-loss rule"),
  takeProfitRule: longText("take-profit rule"),
  riskRule: longText("risk rule"),
  noTradeFilters: z11.string().trim().max(2e4).nullish(),
  checklist: z11.array(checklistItemSchema).min(1, "Add at least one checklist item").max(30, "At most 30 checklist items"),
  examples: z11.array(strategyExampleSchema).max(12).default([]),
  relatedItemIds: z11.array(id2).max(20).default([]),
  changelog: z11.string().trim().max(2e3).nullish()
});
var strategyCreateSchema = z11.object({
  code: z11.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9-]{1,11}$/, "Use 2\u201312 letters, digits or dashes, e.g. VMS"),
  name: z11.string().trim().min(2, "Give the strategy a name").max(190),
  description: itemFields.description,
  collectionId: itemFields.collectionId,
  sortOrder: itemFields.sortOrder,
  tags,
  level: itemFields.level,
  audience: audienceSchema,
  content: strategyContentSchema,
  publish: z11.boolean().default(false)
});
var checklistAnswersSchema = z11.object({
  strategyVersionId: id2.nullable(),
  answers: z11.array(z11.object({ itemId: z11.string().min(1).max(24), checked: z11.boolean() })).max(30).default([])
});
var learningAssignSchema = z11.object({ audience: audienceSchema, dueAt: z11.coerce.date().nullish() });
var learningProgressSchema = z11.object({
  action: z11.enum(["opened", "completed"]),
  /** optional short note when finishing, e.g. "Read and understood" */
  acknowledgement: z11.string().trim().max(500).nullish()
});
function scoreChecklist(checklist, answers) {
  const byId = new Map(answers.map((a) => [a.itemId, a.checked]));
  const items = checklist.map((c) => ({ id: c.id, text: c.text, required: !!c.required, checked: byId.get(c.id) === true, weight: c.weight && c.weight > 0 ? c.weight : 1 }));
  const totalWeight = items.reduce((s, i) => s + i.weight, 0);
  const tickedWeight = items.filter((i) => i.checked).reduce((s, i) => s + i.weight, 0);
  const score = totalWeight > 0 ? (Math.round(tickedWeight / totalWeight * 1e4) / 100).toFixed(2) : "0.00";
  return {
    items: items.map(({ weight: _w, ...rest }) => rest),
    ticked: items.filter((i) => i.checked).length,
    total: items.length,
    score,
    unticked: items.filter((i) => !i.checked).map(({ id: itemId, text: text3, required }) => ({ id: itemId, text: text3, required }))
  };
}

// src/analytics.ts
import { DateTime as DateTime7 } from "luxon";
import { z as z12 } from "zod";
var id3 = z12.string().trim().min(1).max(64);
var optionalId = z12.preprocess((v) => v === "" ? void 0 : v, id3.optional());
var REVIEW_VERDICTS = ["YES", "PARTLY", "NO"];
var analysisReviewSchema = z12.object({
  sections: z12.array(
    z12.object({
      instrumentId: id3,
      biasCorrect: z12.enum(REVIEW_VERDICTS),
      /** null when the section had no key levels to judge */
      levelsRespected: z12.enum(REVIEW_VERDICTS).nullish().transform((v) => v ?? null),
      notes: z12.string().trim().max(2e3).nullish().transform((v) => v ? v : null)
    })
  ).min(1, "Review at least one symbol").max(60)
}).refine((b) => new Set(b.sections.map((s) => s.instrumentId)).size === b.sections.length, { message: "Each symbol can be reviewed once per save", path: ["sections"] });
function analysisReviewOpensAt(def2, day) {
  const zone = def2.analysisDeadline.mode === "FIXED_TIME" ? def2.analysisDeadline.tz : def2.timezone;
  const midnight = DateTime7.fromISO(day, { zone }).startOf("day").plus({ days: 1 }).toUTC().toJSDate();
  const lastClose = sessionsOn(def2, day).reduce((m, w) => !m || w.close > m ? w.close : m, null);
  return lastClose && lastClose < midnight ? lastClose : midnight;
}
var isReviewableSection = (s) => !s.noView && !!s.bias;
var tally = (vs) => ({ yes: vs.filter((v) => v === "YES").length, partly: vs.filter((v) => v === "PARTLY").length, no: vs.filter((v) => v === "NO").length });
var scorePct = (c) => {
  const n = c.yes + c.partly + c.no;
  return n ? new Dec(c.yes).plus(new Dec(c.partly).div(2)).div(n).mul(100).toFixed(2) : null;
};
function analysisAccuracy(reviews) {
  const done = reviews.filter((r) => r.biasCorrect !== null);
  const bias = tally(done.map((r) => r.biasCorrect));
  const levels = tally(done.map((r) => r.levelsRespected));
  return { reviewed: done.length, biasPct: scorePct(bias), bias, levelsReviewed: levels.yes + levels.partly + levels.no, levelsPct: scorePct(levels), levels };
}
var byClose = (a, b) => new Date(a.closedAt).getTime() - new Date(b.closedAt).getTime() || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
var sum = (xs) => xs.reduce((s, x) => s.plus(x), new Dec(0));
var fixed = (d, dp = 2) => d === null ? null : d.isFinite() ? d.toFixed(dp) : "Infinity";
function maxDrawdown(steps) {
  let running = new Dec(0);
  let peak = new Dec(0);
  let worst = new Dec(0);
  for (const s of steps) {
    running = running.plus(s);
    if (running.gt(peak)) peak = running;
    const fall = peak.minus(running);
    if (fall.gt(worst)) worst = fall;
  }
  return worst;
}
function profitFactor(values) {
  const gains = sum(values.filter((v) => v.gt(0)));
  const losses = sum(values.filter((v) => v.lt(0))).abs();
  if (losses.isZero()) return gains.isZero() ? null : new Dec(Infinity);
  return gains.div(losses);
}
function performanceMetrics(trades) {
  const ordered = [...trades].sort(byClose);
  const pnl = ordered.map((t) => dec(t.pnl));
  const wins = pnl.filter((p) => p.gt(0)).length;
  const losses = pnl.filter((p) => p.lt(0)).length;
  const breakevens = pnl.length - wins - losses;
  const rs = ordered.filter((t) => t.r !== null && t.r !== void 0 && t.r !== "").map((t) => dec(t.r));
  const n = rs.length;
  const winR = rs.filter((r) => r.gt(0));
  const lossR = rs.filter((r) => r.lt(0));
  const avgWinR = winR.length ? sum(winR).div(winR.length) : null;
  const avgLossR = lossR.length ? sum(lossR).div(lossR.length) : null;
  const expectancy = n ? sum(winR).plus(sum(lossR)).div(n) : null;
  return {
    trades: ordered.length,
    wins,
    losses,
    breakevens,
    winRate: ordered.length ? new Dec(wins).div(ordered.length).mul(100).toFixed(2) : null,
    rTrades: n,
    totalR: n ? sum(rs).toFixed(2) : null,
    avgR: n ? sum(rs).div(n).toFixed(2) : null,
    avgWinR: fixed(avgWinR),
    avgLossR: fixed(avgLossR),
    expectancyR: fixed(expectancy),
    profitFactorR: n ? fixed(profitFactor(rs)) : null,
    maxDrawdownR: n ? maxDrawdown(rs).toFixed(2) : null
  };
}
function moneyMetrics(trades) {
  const ordered = [...trades].sort(byClose);
  const amounts = ordered.filter((t) => t.amount !== null).map((t) => dec(t.amount));
  const net = sum(amounts);
  return {
    trades: amounts.length,
    excluded: ordered.length - amounts.length,
    net: net.toFixed(2),
    grossProfit: sum(amounts.filter((a) => a.gt(0))).toFixed(2),
    grossLoss: sum(amounts.filter((a) => a.lt(0))).toFixed(2),
    expectancy: amounts.length ? net.div(amounts.length).toFixed(2) : null,
    profitFactor: fixed(profitFactor(amounts)),
    maxDrawdown: amounts.length ? maxDrawdown(amounts).toFixed(2) : null
  };
}
var ANALYTICS_GROUPS = ["trader", "account", "market", "strategy"];
var ANALYTICS_GROUP_LABEL = { trader: "Trader", account: "Account", market: "Market", strategy: "Strategy" };
var NO_STRATEGY = "none";
var analyticsQuerySchema = periodRules(
  z12.object({
    ...periodFields,
    groupBy: z12.enum(ANALYTICS_GROUPS).default("trader"),
    traderId: optionalId,
    accountId: optionalId,
    marketId: optionalId,
    strategyId: optionalId,
    currency: z12.preprocess((v) => v === "" ? void 0 : v, z12.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).optional())
  })
);
var OTHER_STRATEGIES_LABEL = "Other strategies";
var CSV_HEADER = [
  "period_from",
  "period_to",
  "group",
  "name",
  "detail",
  "closed_trades",
  "wins",
  "losses",
  "breakevens",
  "win_rate_pct",
  "trades_with_r",
  "total_r",
  "avg_r",
  "avg_win_r",
  "avg_loss_r",
  "expectancy_r",
  "profit_factor_r",
  "max_drawdown_r",
  "currency",
  "fx_rate_date",
  "net_pnl",
  "gross_profit",
  "gross_loss",
  "expectancy_per_trade",
  "profit_factor",
  "max_drawdown",
  "trades_not_converted",
  "analyses_reviewed",
  "bias_accuracy_pct",
  "levels_reviewed",
  "levels_accuracy_pct"
];
var cell = (v) => v === null || v === void 0 ? "" : typeof v === "number" || /^-?\d+(\.\d+)?$/.test(v) ? String(v) : csvSafeCell(v);
function analyticsCsv(report) {
  const line = (group, r) => {
    const m = r.metrics;
    const $ = r.money;
    const a = r.accuracy;
    return [
      report.period.fromDay,
      report.period.toDay,
      group,
      r.label,
      r.sub,
      m.trades,
      m.wins,
      m.losses,
      m.breakevens,
      m.winRate,
      m.rTrades,
      m.totalR,
      m.avgR,
      m.avgWinR,
      m.avgLossR,
      m.expectancyR,
      m.profitFactorR,
      m.maxDrawdownR,
      $ ? report.currency : null,
      $ ? report.rateDate : null,
      $?.net,
      $?.grossProfit,
      $?.grossLoss,
      $?.expectancy,
      $?.profitFactor,
      $?.maxDrawdown,
      $?.excluded,
      a?.reviewed,
      a?.biasPct,
      a?.levelsReviewed,
      a?.levelsPct
    ].map(cell).join(",");
  };
  return [CSV_HEADER.join(","), ...report.rows.map((r) => line(report.groupBy, r)), line("total", report.total)].join("\r\n") + "\r\n";
}
var CHECKLIST_BAND_LABEL = { all_ticked: "Every checklist item ticked", some_unticked: "Some items left unticked", no_checklist: "No checklist saved" };
function checklistBand(score) {
  if (score === null || score === void 0 || score === "") return "no_checklist";
  return dec(score).gte(100) ? "all_ticked" : "some_unticked";
}
function strategyStats(trades, minTrades) {
  const enough = trades.length >= minTrades;
  if (!enough) return { closedTrades: trades.length, minTrades, enough, winRate: null, avgR: null, rTrades: null, bands: null };
  const all = performanceMetrics(trades);
  const bands = ["all_ticked", "some_unticked", "no_checklist"].map((band) => {
    const mine = trades.filter((t) => checklistBand(t.checklistScore) === band);
    const ok = mine.length >= minTrades;
    const m = ok ? performanceMetrics(mine) : null;
    return { band, trades: mine.length, enough: ok, winRate: m?.winRate ?? null, avgR: m?.avgR ?? null, rTrades: m?.rTrades ?? null };
  }).filter((b) => b.trades > 0);
  return { closedTrades: trades.length, minTrades, enough, winRate: all.winRate, avgR: all.avgR, rTrades: all.rTrades, bands };
}

// src/live.ts
import { z as z13 } from "zod";
var ROOM_PREFIX = "vtf-room-";
var WEBINAR_PREFIX = "vtf-webinar-";
var liveRoomName = (roomId) => `${ROOM_PREFIX}${roomId}`;
var webinarRoomName = (webinarId) => `${WEBINAR_PREFIX}${webinarId}`;
function parseLiveRoomName(name) {
  if (name.startsWith(ROOM_PREFIX) && name.length > ROOM_PREFIX.length) return { type: "room", id: name.slice(ROOM_PREFIX.length) };
  if (name.startsWith(WEBINAR_PREFIX) && name.length > WEBINAR_PREFIX.length) return { type: "webinar", id: name.slice(WEBINAR_PREFIX.length) };
  return null;
}
var GUEST_IDENTITY_PREFIX = "guest:";
var guestIdentity = (guestId) => `${GUEST_IDENTITY_PREFIX}${guestId}`;
var parseIdentity = (identity) => identity.startsWith(GUEST_IDENTITY_PREFIX) ? { kind: "guest", id: identity.slice(GUEST_IDENTITY_PREFIX.length) } : { kind: "user", id: identity };
var LIVE_TOKEN_TTL_SECONDS = 600;
var ALL_SOURCES = ["camera", "microphone", "screen_share", "screen_share_audio"];
var ROOM_SHARE_MODES = ["OPEN", "REQUEST"];
var ROOM_SHARE_MODE_LABEL = { OPEN: "Anyone can share", REQUEST: "Request to share" };
function liveGrant(input) {
  const base = { room: input.room, roomJoin: true, canSubscribe: true, canPublishData: false, roomAdmin: false, hidden: false };
  switch (input.role) {
    case "DESK_MODERATOR":
    case "WEBINAR_HOST":
    case "WEBINAR_COHOST":
      return { ...base, canPublish: true, canPublishSources: [...ALL_SOURCES] };
    case "DESK_MEMBER": {
      const may = (input.shareMode ?? "OPEN") === "OPEN" || input.shareGranted === true;
      return { ...base, canPublish: may, canPublishSources: may ? [...ALL_SOURCES] : [] };
    }
    case "WEBINAR_STAGE":
      return { ...base, canPublish: true, canPublishSources: ["camera", "microphone"] };
    case "WEBINAR_ATTENDEE":
      return { ...base, canPublish: false, canPublishSources: [] };
  }
}
var grantPermission = (g) => ({ canPublish: g.canPublish, canPublishSources: g.canPublishSources, canSubscribe: true, canPublishData: false });
var WEBINAR_AUDIENCES = ["FLOOR", "MARKETS", "SELECTED", "GUESTS"];
var WEBINAR_AUDIENCE_LABEL = { FLOOR: "Whole floor", MARKETS: "Market desks", SELECTED: "Selected traders", GUESTS: "Invited guests only" };
var WEBINAR_STATUSES = ["SCHEDULED", "LIVE", "ENDED", "CANCELLED"];
var WEBINAR_LAYOUTS = ["CHART_WEBCAM", "SPEAKER", "SIDE_BY_SIDE"];
var WEBINAR_LAYOUT_LABEL = { CHART_WEBCAM: "Chart screen + webcam inset", SPEAKER: "Speaker only", SIDE_BY_SIDE: "Side by side" };
var WEBINAR_BACKSTAGE_MINUTES = 30;
var CONTEXT_TYPES = ["TRADE", "SETUP", "ANALYSIS"];
var text2 = (max) => z13.string().trim().max(max, `Keep this under ${max} characters`);
var id4 = z13.string().trim().min(1).max(40);
var ids = (max) => z13.array(id4).max(max).default([]).transform((v) => [...new Set(v)]);
var LIVE_LIMITS = { roomName: 80, title: 160, agenda: 5e3, chat: 500, question: 300, members: 60, coHosts: 5, attachments: 10, guestEmails: 20, guestName: 120 };
var L2 = LIVE_LIMITS;
var roomCreateSchema = z13.object({ name: text2(L2.roomName).min(2, "Give the room a name"), memberIds: ids(L2.members) });
var roomModeSchema = z13.object({ shareMode: z13.enum(ROOM_SHARE_MODES) });
var roomInviteSchema = z13.object({ memberIds: ids(L2.members).pipe(z13.array(id4).min(1, "Choose at least one person")) });
var discussSchema = z13.object({ contextType: z13.enum(CONTEXT_TYPES), contextId: id4 });
var shareDecisionSchema = z13.object({ decision: z13.enum(["GRANT", "DENY", "REVOKE"]) });
var roomModerationSchema = z13.object({ action: z13.enum(["MUTE", "STOP_SHARE", "REMOVE"]) });
var webinarFields = z13.object({
  title: text2(L2.title).min(3, "Give the webinar a title"),
  description: text2(L2.agenda).nullish().transform((v) => v ? v : null),
  startsAt: z13.string().refine((v) => !Number.isNaN(Date.parse(v)), "Enter a valid date and time"),
  durationMinutes: z13.number().int().min(15, "At least 15 minutes").max(480, "At most 8 hours"),
  audienceMode: z13.enum(WEBINAR_AUDIENCES),
  marketIds: ids(10),
  userIds: ids(100),
  coHostIds: ids(L2.coHosts),
  libraryItemIds: ids(L2.attachments)
});
function audienceRules(v, ctx) {
  if (v.audienceMode === "MARKETS" && !v.marketIds?.length) ctx.addIssue({ code: "custom", path: ["marketIds"], message: "Choose at least one market desk" });
  if (v.audienceMode === "SELECTED" && !v.userIds?.length) ctx.addIssue({ code: "custom", path: ["userIds"], message: "Choose at least one trader" });
}
var webinarInputSchema = webinarFields.superRefine(audienceRules);
var webinarUpdateSchema = webinarFields.partial().superRefine(audienceRules);
var webinarLayoutSchema = z13.object({ layout: z13.enum(WEBINAR_LAYOUTS) });
var chatSchema = z13.object({ body: text2(L2.chat).min(1, "Write a message") });
var questionSchema = z13.object({ body: text2(L2.question).min(3, "Ask a question") });
var questionModerateSchema = z13.object({ action: z13.enum(["ANSWER", "REOPEN", "HIDE", "UNHIDE"]) });
var chatModerateSchema = z13.object({ action: z13.enum(["HIDE", "UNHIDE"]) });
var stageSchema = z13.object({ onStage: z13.boolean() });
var guestInviteSchema = z13.object({ emails: z13.array(z13.string().trim().toLowerCase().email().max(254)).min(1).max(L2.guestEmails).transform((v) => [...new Set(v)]) });
var guestRegisterSchema = z13.object({
  name: text2(L2.guestName).min(2, "Enter your name"),
  email: z13.string().trim().toLowerCase().email("Enter a valid email").max(254),
  acceptedPolicyId: id4,
  website: z13.string().max(200).optional()
  // honeypot
});
var reminderOffsetsSchema = z13.array(z13.number().int().min(1).max(10080)).min(0).max(6);
function webinarReminderTimes(startsAt, offsetsMinutes, now) {
  return [...new Set(offsetsMinutes)].sort((a, b) => b - a).map((offset) => ({ offset, at: new Date(startsAt.getTime() - offset * 6e4) })).filter((r) => r.at.getTime() > now.getTime());
}
function offsetLabel(minutes) {
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes === 1440 ? "" : "s"}`;
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes === 60 ? "" : "s"}`;
  return `${minutes} minutes`;
}
var webinarIsOpenForAudience = (w) => w.status === "LIVE";
var icsText = (s) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
var icsDate = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
function fold(line) {
  const bytes = Buffer.from(line, "utf8");
  if (bytes.length <= 75) return line;
  const out = [];
  let cur = "";
  let curLen = 0;
  let limit = 75;
  for (const ch of line) {
    const n = Buffer.byteLength(ch, "utf8");
    if (curLen + n > limit) {
      out.push(cur);
      cur = "";
      curLen = 0;
      limit = 74;
    }
    cur += ch;
    curLen += n;
  }
  out.push(cur);
  return out.join("\r\n ");
}
function buildWebinarIcs(w, now) {
  const end = new Date(w.startsAt.getTime() + w.durationMinutes * 6e4);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//K5 Trading//Virtual Trading Floor//EN",
    "CALSCALE:GREGORIAN",
    `METHOD:${w.status === "CANCELLED" ? "CANCEL" : "PUBLISH"}`,
    "BEGIN:VEVENT",
    `UID:webinar-${w.id}@vtf`,
    `DTSTAMP:${icsDate(now)}`,
    `DTSTART:${icsDate(w.startsAt)}`,
    `DTEND:${icsDate(end)}`,
    `SUMMARY:${icsText(w.title)}`,
    `DESCRIPTION:${icsText([w.description ?? "", `Host: ${w.hostName}`, `Join: ${w.url}`].filter(Boolean).join("\n\n"))}`,
    `LOCATION:${icsText("VTF live webinar")}`,
    `URL:${w.url}`,
    `STATUS:${w.status === "CANCELLED" ? "CANCELLED" : "CONFIRMED"}`,
    "END:VEVENT",
    "END:VCALENDAR"
  ];
  return lines.map(fold).join("\r\n") + "\r\n";
}

// src/streaming.ts
import { z as z14 } from "zod";
var DESTINATION_KINDS = ["YOUTUBE", "FACEBOOK", "RTMP"];
var DESTINATION_KIND_LABEL = { YOUTUBE: "YouTube", FACEBOOK: "Facebook Page", RTMP: "Custom RTMP" };
var FACEBOOK_RTMPS_URL = "rtmps://live-api-s.facebook.com:443/rtmp/";
var YOUTUBE_SCOPES = ["https://www.googleapis.com/auth/youtube", "https://www.googleapis.com/auth/youtube.upload"];
var YOUTUBE_CALLBACK_PATH = "/api/v1/destinations/youtube/callback";
var rtmpUrl = z14.string().trim().max(500, "Keep the server URL under 500 characters").refine((v) => {
  try {
    const u = new URL(v);
    return (u.protocol === "rtmp:" || u.protocol === "rtmps:") && !!u.hostname && !u.username && !u.password;
  } catch {
    return false;
  }
}, "Enter an rtmp:// or rtmps:// server URL");
var rtmpDestinationSchema = z14.object({
  kind: z14.enum(["FACEBOOK", "RTMP"]),
  name: z14.string().trim().min(2, "Give the destination a name").max(80),
  url: rtmpUrl,
  streamKey: z14.string().trim().min(4, "Paste the stream key").max(500).regex(/^[^\s]+$/, "A stream key has no spaces")
}).superRefine((v, ctx) => {
  if (v.kind === "FACEBOOK" && !v.url.startsWith("rtmps://")) ctx.addIssue({ code: "custom", path: ["url"], message: "Facebook only accepts rtmps:// (secure) server URLs" });
});
function maskSecret(last4) {
  if (!last4) return null;
  return `\u2022\u2022\u2022\u2022${last4}`;
}
var lastFour = (secret) => secret.slice(-4);
function egressRtmpUrl(server, streamKey) {
  return `${server.replace(/\/+$/, "")}/${streamKey}`;
}
function redactRtmp(url) {
  const i = url.lastIndexOf("/");
  return i > url.indexOf("://") + 2 ? `${url.slice(0, i + 1)}\u2022\u2022\u2022\u2022` : url;
}
var STREAM_PRIVACIES = ["PUBLIC", "UNLISTED"];
var STREAM_PRIVACY_LABEL = { PUBLIC: "Public", UNLISTED: "Unlisted" };
var broadcastSchema = z14.object({
  /** YouTube allows 100 characters and no angle brackets */
  title: z14.string().trim().min(3, "Give the broadcast a title").max(100, "YouTube allows 100 characters").regex(/^[^<>]*$/, "Titles can\u2019t contain < or >"),
  description: z14.string().trim().max(4500, "Keep it under 4,500 characters (the disclaimer is added after it)").nullish().transform((v) => v || null),
  privacy: z14.enum(STREAM_PRIVACIES),
  destinationIds: z14.array(z14.string().trim().min(1).max(40)).max(6, "At most 6 destinations").default([]).transform((v) => [...new Set(v)])
});
var slateSchema = z14.object({ slate: z14.enum(["START", "END"]).nullable() });
var broadcastLinksSchema = z14.object({
  facebookUrl: z14.string().trim().max(500).url("Enter the full video link").refine((v) => /^https:\/\/(www\.|m\.)?(facebook\.com|fb\.watch)\//.test(v), "Use a facebook.com or fb.watch link").nullable()
});
var OUTPUT_STATUSES = ["IDLE", "CONNECTING", "LIVE", "ERROR", "STOPPED"];
var OUTPUT_STATUS_LABEL = { IDLE: "Not started", CONNECTING: "Connecting", LIVE: "Live", ERROR: "Error", STOPPED: "Stopped" };
var OUTPUT_STATUS_TONE = { IDLE: "neutral", CONNECTING: "warn", LIVE: "up", ERROR: "down", STOPPED: "neutral" };
function outputStatusAfter(current, phase, stopRequested) {
  if (current === "STOPPED" || current === "IDLE") return current;
  switch (phase) {
    case "starting":
      return current === "ERROR" ? "ERROR" : current === "LIVE" ? "LIVE" : "CONNECTING";
    case "active":
      return current === "ERROR" ? "ERROR" : stopRequested ? current : "LIVE";
    case "ending":
      return current;
    case "complete":
      return stopRequested ? "STOPPED" : "ERROR";
    case "failed":
      return stopRequested ? "STOPPED" : "ERROR";
  }
}
function streamRetryDelaySeconds(attempt) {
  return Math.min(300, 30 * 2 ** Math.max(0, attempt - 1));
}
var RECORDING_STATUSES = ["STARTING", "RECORDING", "PROCESSING", "READY", "FAILED"];
var RECORDING_STATUS_LABEL = { STARTING: "Starting", RECORDING: "Recording", PROCESSING: "Processing", READY: "Ready", FAILED: "Failed" };
var RECORDING_STATUS_TONE = { STARTING: "warn", RECORDING: "down", PROCESSING: "accent", READY: "up", FAILED: "down" };
function recordingStatusAfter(current, phase, hasFile = true) {
  if (current === "READY" || current === "FAILED") return current;
  switch (phase) {
    case "starting":
      return current;
    case "active":
      return current === "STARTING" ? "RECORDING" : current;
    case "ending":
      return "PROCESSING";
    case "complete":
      return hasFile ? "READY" : "FAILED";
    case "failed":
      return "FAILED";
  }
}
function recordingKey(target, recordingId) {
  const folder = target.webinarId ? `webinars/${target.webinarId}` : `rooms/${target.roomId ?? "unknown"}`;
  return `recordings/${folder}/${recordingId}.mp4`;
}
var recordingTrimSchema = z14.object({
  trimStartSeconds: z14.number().int().min(0).max(24 * 3600).nullable(),
  trimEndSeconds: z14.number().int().min(1).max(24 * 3600).nullable()
}).superRefine((v, ctx) => {
  if (v.trimStartSeconds !== null && v.trimEndSeconds !== null && v.trimEndSeconds <= v.trimStartSeconds) ctx.addIssue({ code: "custom", path: ["trimEndSeconds"], message: "The end must be after the start" });
});
var recordingLibrarySchema = z14.object({ action: z14.enum(["PUBLISH", "UNPUBLISH"]) });
function trimProblem(t, durationSeconds) {
  if (durationSeconds === null) return null;
  if (t.trimStartSeconds !== null && t.trimStartSeconds >= durationSeconds) return "The start is after the end of the recording.";
  if (t.trimEndSeconds !== null && t.trimEndSeconds > durationSeconds) return "The end is after the end of the recording.";
  return null;
}
function mediaFragment(t) {
  if (t.trimStartSeconds === null && t.trimEndSeconds === null) return "";
  return `#t=${t.trimStartSeconds ?? 0}${t.trimEndSeconds !== null ? `,${t.trimEndSeconds}` : ""}`;
}
function fmtDuration(seconds) {
  if (seconds === null || seconds === void 0 || !Number.isFinite(seconds)) return "\u2014";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor(s % 3600 / 60);
  const r = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`;
}
function parseClock(v) {
  const t = v.trim();
  if (!t) return null;
  if (!/^\d+(:\d{1,2}){0,2}$/.test(t)) return NaN;
  return t.split(":").reduce((acc, p) => acc * 60 + Number(p), 0);
}
var EGRESS_LAYOUT_PATH = "/egress/layout";
var EGRESS_OVERLAY_POLL_SECONDS = 5;
var EGRESS_START_SLATE_SECONDS = 10;
var egressLayoutParam = (layout) => layout.toLowerCase().replace(/_/g, "-");
function parseEgressLayout(v) {
  const up = (v ?? "").toUpperCase().replace(/-/g, "_");
  return WEBINAR_LAYOUTS.includes(up) ? up : "CHART_WEBCAM";
}
function overlayFor(i) {
  const full = (i.fullDisclaimer?.trim() || i.shortDisclaimer).trim();
  const slateBody = (kind) => ({ heading: kind === "START" ? `${i.brandName} \xB7 live` : `Thanks for watching \xB7 ${i.brandName}`, title: i.title, body: full, operator: i.operator });
  return {
    layout: i.layout,
    brand: i.brandName,
    lowerThird: i.presenterName ? { name: i.presenterName, title: i.title } : null,
    ticker: i.isPublic ? i.shortDisclaimer : null,
    slate: i.isPublic && i.slate ? { kind: i.slate, ...slateBody(i.slate) } : null,
    startSlate: i.isPublic ? slateBody("START") : null
  };
}
function broadcastDescription(description, fullDisclaimer, shortDisclaimer) {
  const disclaimer = (fullDisclaimer?.trim() || shortDisclaimer).trim().slice(0, 5e3);
  const room = 5e3 - disclaimer.length - 2;
  const text3 = description?.trim() ? description.trim().slice(0, Math.max(0, room)) : "";
  return [text3, disclaimer].filter(Boolean).join("\n\n");
}

// src/publishing.ts
import { z as z15 } from "zod";
var PUBLISH_STATUSES = ["PENDING_APPROVAL", "CHANGES_REQUESTED", "APPROVED", "PUBLISHING", "PUBLISHED", "FAILED", "REJECTED"];
var PUBLISH_STATUS_LABEL = {
  PENDING_APPROVAL: "Waiting for approval",
  CHANGES_REQUESTED: "Changes requested",
  APPROVED: "Approved",
  PUBLISHING: "Publishing",
  PUBLISHED: "Published",
  FAILED: "Publishing failed",
  REJECTED: "Rejected"
};
var PUBLISH_STATUS_TONE = {
  PENDING_APPROVAL: "warn",
  CHANGES_REQUESTED: "accent",
  APPROVED: "accent",
  PUBLISHING: "accent",
  PUBLISHED: "up",
  FAILED: "down",
  REJECTED: "neutral"
};
var PUBLISH_ACTIONS = ["EDIT", "APPROVE", "REQUEST_CHANGES", "RESUBMIT", "REJECT", "START", "COMPLETE", "FAIL", "RETRY", "REOPEN"];
var MOVES = {
  PENDING_APPROVAL: { EDIT: "PENDING_APPROVAL", APPROVE: "APPROVED", REQUEST_CHANGES: "CHANGES_REQUESTED", REJECT: "REJECTED" },
  CHANGES_REQUESTED: { EDIT: "CHANGES_REQUESTED", RESUBMIT: "PENDING_APPROVAL", REJECT: "REJECTED" },
  APPROVED: { START: "PUBLISHING" },
  PUBLISHING: { START: "PUBLISHING", COMPLETE: "PUBLISHED", FAIL: "FAILED" },
  // a failed publish can be tried again on the same approval, or reopened for editing (and a fresh approval)
  FAILED: { RETRY: "APPROVED", REOPEN: "PENDING_APPROVAL", REJECT: "REJECTED" },
  PUBLISHED: {},
  REJECTED: {}
};
function publishStatusAfter(status, action) {
  return MOVES[status][action] ?? null;
}
var PUBLISH_DECISIONS = ["SUBMITTED", "APPROVED", "CHANGES_REQUESTED", "RESUBMITTED", "REJECTED", "REOPENED", "RETRIED"];
var PUBLISH_DECISION_LABEL = {
  SUBMITTED: "Package prepared",
  APPROVED: "Approved",
  CHANGES_REQUESTED: "Changes requested",
  RESUBMITTED: "Resubmitted",
  REJECTED: "Rejected",
  REOPENED: "Reopened for editing",
  RETRIED: "Publish tried again"
};
function publishBlocker(i) {
  if (i.status !== "APPROVED" && i.status !== "PUBLISHING") return `The package is \u201C${PUBLISH_STATUS_LABEL[i.status]}\u201D, not approved.`;
  if (!i.approvalId) return "There is no approval for this publish.";
  const latest = [...i.decisions].reverse().find((d) => d.kind !== "RETRIED");
  if (!latest || latest.kind !== "APPROVED") return "The latest decision on this package is not an approval.";
  if (latest.id !== i.approvalId) return "This publish was queued for an older approval.";
  if (!latest.hasSnapshot) return "The approval has no record of the metadata approved.";
  return null;
}
var PUBLISH_PRIVACIES = ["PUBLIC", "UNLISTED", "SCHEDULED"];
var PUBLISH_PRIVACY_LABEL = { PUBLIC: "Public", UNLISTED: "Unlisted", SCHEDULED: "Scheduled" };
var PUBLISH_MODES = ["UPLOAD", "UPDATE_LIVE"];
var SCHEDULE_MIN_LEAD_MINUTES = 10;
var privacyFromDefault = (v) => v === "unlisted" ? "UNLISTED" : "PUBLIC";
function youtubeStatus(privacy, publishAt) {
  if (privacy === "SCHEDULED") return { privacyStatus: "private", ...publishAt ? { publishAt } : {} };
  return { privacyStatus: privacy === "PUBLIC" ? "public" : "unlisted" };
}
function scheduleProblem(privacy, publishAt, now) {
  if (privacy !== "SCHEDULED") return null;
  if (!publishAt) return "Pick the date and time the video goes public.";
  const t = Date.parse(publishAt);
  if (!Number.isFinite(t)) return "That date and time can\u2019t be read.";
  if (t < now.getTime() + SCHEDULE_MIN_LEAD_MINUTES * 6e4) return `Pick a time at least ${SCHEDULE_MIN_LEAD_MINUTES} minutes from now.`;
  if (t > now.getTime() + 366 * 24 * 36e5) return "Pick a time within the next year.";
  return null;
}
function privacyForcedPrivate(requested, returned) {
  return requested !== "SCHEDULED" && returned === "private";
}
var YOUTUBE_TAGS_MAX_CHARS = 450;
var tagList = z15.array(z15.string().trim().min(1).max(60, "Keep each tag under 60 characters").regex(/^[^<>,]*$/, "Tags can\u2019t contain <, > or commas")).max(30, "At most 30 tags").transform((v) => [...new Set(v)]).refine((v) => v.reduce((n, t) => n + t.length + (t.includes(" ") ? 2 : 0) + 1, 0) <= YOUTUBE_TAGS_MAX_CHARS, `YouTube allows about ${YOUTUBE_TAGS_MAX_CHARS} characters of tags in total`);
var publishPackageSchema = z15.object({
  /** YouTube allows 100 characters and no angle brackets */
  title: z15.string().trim().min(3, "Give the video a title").max(100, "YouTube allows 100 characters").regex(/^[^<>]*$/, "Titles can\u2019t contain < or >"),
  /** the text above the disclaimer; the full §9a disclaimer is appended automatically and never typed here */
  description: z15.string().trim().max(4500, "Keep it under 4,500 characters (the disclaimer is added after it)").regex(/^[^<>]*$/, "YouTube descriptions can\u2019t contain < or >").nullish().transform((v) => v ?? ""),
  tags: tagList,
  playlistId: z15.string().trim().regex(/^[A-Za-z0-9_-]{10,64}$/, "Paste the playlist id (from the playlist\u2019s link, after list=)").nullish().or(z15.literal("")).transform((v) => v || null),
  privacy: z15.enum(PUBLISH_PRIVACIES),
  /** UTC instant; the browser converts the approver's local time */
  publishAt: z15.iso.datetime({ offset: true }).nullish().transform((v) => v ? new Date(v).toISOString() : null),
  trimStartSeconds: z15.number().int().min(0).max(24 * 3600).nullable(),
  trimEndSeconds: z15.number().int().min(1).max(24 * 3600).nullable(),
  thumbnailId: z15.string().trim().min(1).max(40).nullable()
}).superRefine((v, ctx) => {
  if (v.privacy === "SCHEDULED" && !v.publishAt) ctx.addIssue({ code: "custom", path: ["publishAt"], message: "Pick the date and time the video goes public" });
  if (v.trimStartSeconds !== null && v.trimEndSeconds !== null && v.trimEndSeconds <= v.trimStartSeconds) ctx.addIssue({ code: "custom", path: ["trimEndSeconds"], message: "The end must be after the start" });
}).transform((v) => ({ ...v, publishAt: v.privacy === "SCHEDULED" ? v.publishAt : null }));
var publishApproveSchema = z15.object({ version: z15.number().int().min(1) });
var publishChangesSchema = z15.object({ notes: z15.string().trim().min(3, "Say what needs changing").max(2e3) });
var publishRejectSchema = z15.object({ notes: z15.string().trim().max(2e3).nullish().transform((v) => v || null) });
var publishSubmitSchema = z15.object({ notes: z15.string().trim().max(2e3).nullish().transform((v) => v || null) });
var publishRetrySchema = z15.object({ action: z15.enum(["RETRY", "REOPEN"]) });
var publishDescription = (body2, fullDisclaimer, shortDisclaimer) => broadcastDescription(body2, fullDisclaimer, shortDisclaimer);
var fmtDay = (d) => `${d.getUTCDate()} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()]} ${d.getUTCFullYear()}`;
function draftPublishDescription(i) {
  const parts = [];
  if (i.agenda?.trim()) parts.push(i.agenda.trim());
  const co = (i.coHostNames ?? []).filter(Boolean);
  parts.push(`Presented by ${i.presenterName}${co.length ? ` with ${co.join(", ")}` : ""} \xB7 ${i.brandName}. Recorded ${i.live === false ? "" : "live "}on ${fmtDay(i.recordedAt)}.`);
  const links = (i.links ?? []).filter((l) => /^https?:\/\//.test(l.url));
  if (links.length) parts.push(["Links:", ...links.map((l) => `\u2022 ${l.label}: ${l.url}`)].join("\n"));
  return parts.join("\n\n").replace(/[<>]/g, "").slice(0, 4500);
}
function draftPublishTags(i) {
  const tags2 = [i.brandName, "trading", "trading education", ...(i.marketCodes ?? []).map((m) => `${m} trading`), i.kind === "webinar" ? "webinar" : "live trading"];
  return [...new Set(tags2.map((t) => t.replace(/[<>,]/g, "").trim()).filter(Boolean))].slice(0, 15);
}
var THUMBNAIL_WIDTH = 1280;
var THUMBNAIL_HEIGHT = 720;
var THUMBNAIL_MAX_BYTES = 2 * 1024 * 1024;
var THUMBNAIL_VARIANT_COUNT = 3;
var THUMBNAIL_VARIANT_LABEL = ["Navy \xB7 chart", "Green \xB7 candles", "Amber \xB7 bold"];
function imageTypeOf(head) {
  if (head.length >= 8 && head[0] === 137 && head[1] === 80 && head[2] === 78 && head[3] === 71 && head[4] === 13 && head[5] === 10 && head[6] === 26 && head[7] === 10) return "image/png";
  if (head.length >= 3 && head[0] === 255 && head[1] === 216 && head[2] === 255) return "image/jpeg";
  return null;
}
function thumbnailKey(recordingKey2, name) {
  return `${recordingKey2.replace(/\.mp4$/, "")}-thumbs/${name}`;
}
var esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
function wrapLines(text3, width, maxLines) {
  const words = text3.trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  let i = 0;
  for (; i < words.length; i++) {
    let w = words[i];
    if (w.length > width) w = `${w.slice(0, width - 1)}\u2026`;
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= width) {
      cur = next;
      continue;
    }
    lines.push(cur);
    cur = w;
    if (lines.length === maxLines) break;
  }
  if (lines.length < maxLines && cur) {
    lines.push(cur);
    i = words.length;
  }
  if (i < words.length && lines.length) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = last.length >= width ? `${last.slice(0, width - 1)}\u2026` : `${last}\u2026`;
  }
  return lines;
}
function thumbnailSvg(variant, i) {
  const W = THUMBNAIL_WIDTH;
  const H = THUMBNAIL_HEIGHT;
  const font = esc(i.fontFamily ?? "Inter");
  const v = (variant % 3 + 3) % 3;
  const palette = [
    { bg1: "#0b1422", bg2: "#16294a", accent: "#4f8cff", ink: "#ffffff", sub: "#b7c6e0", badgeInk: "#0b1422" },
    { bg1: "#06140f", bg2: "#0e3324", accent: "#22c55e", ink: "#ffffff", sub: "#b9dcc8", badgeInk: "#06140f" },
    { bg1: "#f59e0b", bg2: "#fbbf24", accent: "#111827", ink: "#111827", sub: "#3b2f12", badgeInk: "#fbbf24" }
  ][v];
  const size = v === 2 ? 84 : v === 1 ? 70 : 76;
  const lines = wrapLines(i.title, v === 1 ? 17 : 24, 3);
  const titleX = v === 1 ? 560 : 80;
  const titleTop = v === 2 ? 300 : 250;
  const title = lines.map((l, n) => `<text x="${titleX}" y="${titleTop + n * (size + 14)}" font-size="${size}" font-weight="700" fill="${palette.ink}">${esc(l)}</text>`).join("");
  let seed = [...i.title].reduce((h, c) => h * 31 + c.charCodeAt(0) >>> 0, 7 + v);
  const rnd = () => (seed = seed * 1103515245 + 12345 >>> 0) % 1e3 / 1e3;
  const candles = [];
  let y = 470;
  const span = v === 1 ? { x0: 60, n: 11, step: 42 } : { x0: 760, n: 12, step: 40 };
  for (let k = 0; k < span.n; k++) {
    const open = y;
    const close = Math.max(240, Math.min(640, y + (rnd() - 0.55) * 90));
    const hi = Math.min(open, close) - 10 - rnd() * 30;
    const lo = Math.max(open, close) + 10 + rnd() * 30;
    const up = close < open;
    const x = span.x0 + k * span.step;
    const col = v === 2 ? up ? "#111827" : "#7c2d12" : up ? palette.accent : "#ef4444";
    candles.push(`<line x1="${x + 12}" y1="${hi.toFixed(0)}" x2="${x + 12}" y2="${lo.toFixed(0)}" stroke="${col}" stroke-width="3"/><rect x="${x}" y="${Math.min(open, close).toFixed(0)}" width="24" height="${Math.max(4, Math.abs(close - open)).toFixed(0)}" fill="${col}" rx="2"/>`);
    y = close;
  }
  const chartOpacity = v === 1 ? 0.9 : 0.28;
  const badge = `<rect x="${v === 1 ? 560 : 80}" y="80" rx="10" width="${Math.max(180, i.brandName.length * 22 + 48)}" height="60" fill="${palette.accent}"/><text x="${(v === 1 ? 560 : 80) + 24}" y="122" font-size="32" font-weight="700" fill="${palette.badgeInk}">${esc(i.brandName)}</text>`;
  const footY = H - 80;
  const foot = `<text x="${titleX}" y="${footY}" font-size="40" font-weight="700" fill="${palette.ink}">${esc(i.presenterName)}</text><text x="${titleX}" y="${footY + 42}" font-size="28" fill="${palette.sub}">${esc(i.dateLabel)}</text>`;
  const stripe = v === 0 ? `<rect x="0" y="0" width="16" height="${H}" fill="${palette.accent}"/>` : v === 2 ? `<rect x="0" y="${H - 18}" width="${W}" height="18" fill="${palette.accent}"/>` : "";
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="${font}">`,
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${palette.bg1}"/><stop offset="1" stop-color="${palette.bg2}"/></linearGradient></defs>`,
    `<rect width="${W}" height="${H}" fill="url(#g)"/>`,
    `<g opacity="${chartOpacity}">${candles.join("")}</g>`,
    stripe,
    badge,
    title,
    foot,
    "</svg>"
  ].join("");
}

// src/instrument-import.ts
import { z as z16 } from "zod";
var ALIASES = {
  symbol: "symbol",
  ticker: "symbol",
  exchange: "exchange",
  prefix: "exchange",
  currency: "quoteCurrency",
  quotecurrency: "quoteCurrency",
  name: "displayName",
  description: "displayName",
  displayname: "displayName",
  ticksize: "tickSize",
  pointvalue: "pointValue",
  sizeunit: "sizeUnit",
  minsize: "minSize",
  sizestep: "sizeStep",
  tvticker: "tvTicker",
  pricesymbol: "priceSymbol"
};
var headerKey = (h) => h.toLowerCase().replace(/[^a-z]/g, "");
var WATCHLIST_ITEM = /^[A-Z0-9_.]+:[A-Z0-9_.!/&-]+$/i;
var MIN_IMPORT_COLUMNS = ["symbol", "exchange", "currency", "name"];
var MAX_IMPORT_ROWS = 2e3;
var JPY_PAIR_TICK = "0.001";
function defaultTickSize(symbol, d) {
  if (d.sizeUnit === "LOTS" && /^[A-Z]{3}JPY$/.test(symbol)) return JPY_PAIR_TICK;
  return d.tickSize;
}
function issues(e) {
  return e.issues.map((x) => `${x.path.map(String).join(".") || "row"} \u2014 ${x.message}`);
}
function splitSymbol(cell2) {
  const t = cell2.trim().toUpperCase();
  const i = t.indexOf(":");
  return i > 0 ? { exchange: t.slice(0, i), symbol: t.slice(i + 1) } : { exchange: null, symbol: t };
}
function buildRow(line, given, present, market) {
  const label = (given.symbol ?? "").trim();
  const split = splitSymbol(given.symbol ?? "");
  const exchange = split.exchange ?? ((given.exchange ?? "").trim().toUpperCase() || null);
  const symbol = split.symbol;
  const errors = [];
  const createErrors = [];
  const sym = instrumentSchema.shape.symbol.safeParse(symbol);
  if (!sym.success) errors.push(...issues(sym.error).map((m) => m.replace(/^row/, "symbol")));
  const raw = {};
  for (const f of ["displayName", "quoteCurrency", "tickSize", "pointValue", "sizeUnit", "minSize", "sizeStep"]) {
    const v = (given[f] ?? "").trim();
    if (v) raw[f] = f === "sizeUnit" ? v.toUpperCase() : v;
  }
  if (present.has("tvTicker")) raw.tvTicker = (given.tvTicker ?? "").trim();
  else if (exchange && symbol) raw.tvTicker = `${exchange}:${symbol}`;
  if (present.has("priceSymbol")) raw.priceSymbol = (given.priceSymbol ?? "").trim();
  const parsed = instrumentUpdateSchema.safeParse(raw);
  let supplied = {};
  if (parsed.success) {
    const { active: _a, ...rest } = parsed.data;
    supplied = rest;
  } else errors.push(...issues(parsed.error));
  let create = null;
  if (!errors.length) {
    const d = market.defaults;
    const filled = {
      symbol: sym.success ? sym.data : symbol,
      displayName: supplied.displayName ?? symbol,
      quoteCurrency: supplied.quoteCurrency ?? market.currency ?? void 0,
      tickSize: supplied.tickSize ?? defaultTickSize(symbol, d) ?? void 0,
      pointValue: supplied.pointValue ?? d.pointValue ?? void 0,
      sizeUnit: supplied.sizeUnit ?? d.sizeUnit ?? void 0,
      minSize: supplied.minSize ?? d.minSize ?? void 0,
      sizeStep: supplied.sizeStep ?? d.sizeStep ?? void 0,
      ...supplied.tvTicker !== void 0 && { tvTicker: supplied.tvTicker },
      ...supplied.priceSymbol !== void 0 && { priceSymbol: supplied.priceSymbol }
    };
    const need = { quoteCurrency: "currency", tickSize: "tick size", pointValue: "point value", sizeUnit: "size unit" };
    for (const [k, word2] of Object.entries(need)) {
      if (filled[k] === void 0) createErrors.push(`${k} \u2014 required: the file gives no ${word2} and this market has no default ${word2}`);
    }
    if (!createErrors.length) {
      const c = instrumentSchema.omit({ marketId: true }).safeParse(filled);
      if (c.success) create = c.data;
      else createErrors.push(...issues(c.error));
    }
  }
  return { line, label: label || symbol, symbol: sym.success ? sym.data : symbol, exchange, supplied, create, errors, createErrors };
}
function watchlistItems(text3) {
  const items = text3.replace(/^﻿/, "").split(/[,\r\n]+/).map((s) => s.trim()).filter((s) => s && !s.startsWith("###"));
  return items.length && items.every((s) => WATCHLIST_ITEM.test(s)) ? items : null;
}
function parseInstrumentImport(text3, market) {
  const [header, ...body2] = parseCsv(text3);
  if (!header) return { format: "csv", rows: [], error: "The file is empty." };
  const cols = header.map((h) => ALIASES[headerKey(h)] ?? null);
  if (!cols.includes("symbol")) {
    const items = watchlistItems(text3);
    if (items) return { format: "watchlist", rows: items.map((item, i) => buildRow(i + 1, { symbol: item }, /* @__PURE__ */ new Set(), market)), error: null };
    return {
      format: "csv",
      rows: [],
      error: `Missing column: symbol. Use a header row such as ${MIN_IMPORT_COLUMNS.join(",")}, or paste a TradingView watchlist export (e.g. PSX:OGDC,PSX:MARI).`
    };
  }
  const present = new Set(cols.filter((c) => c !== null));
  const rows = body2.map((cells, i) => {
    const given = {};
    cols.forEach((c, j) => {
      if (c && given[c] === void 0) given[c] = cells[j] ?? "";
    });
    return buildRow(i + 2, given, present, market);
  });
  return { format: "csv", rows, error: null };
}
var TEMPLATE_EXAMPLES = {
  PSX: [["OGDC", "PSX", "Oil & Gas Development Co"], ["MARI", "PSX", "Mari Energies"]],
  US: [["AAPL", "NASDAQ", "Apple Inc"], ["KO", "NYSE", "Coca-Cola Co"]],
  FX: [["EURUSD", "OANDA", "Euro / US Dollar"], ["USDJPY", "OANDA", "US Dollar / Japanese Yen"]],
  COMM: [["XAUUSD", "OANDA", "Gold spot"], ["XAGUSD", "OANDA", "Silver spot"]]
};
function instrumentImportTemplate(market, existingTvTickers = []) {
  const prefix = existingTvTickers.map((t) => t && t.includes(":") ? t.slice(0, t.indexOf(":")) : null).find(Boolean) ?? market.code;
  const examples = TEMPLATE_EXAMPLES[market.code] ?? [["SYMBOL1", prefix, "First example"], ["SYMBOL2", prefix, "Second example"]];
  const lines = [MIN_IMPORT_COLUMNS.join(","), ...examples.map(([s, ex, name]) => [s, ex, market.currency ?? "", name].map(csvCell).join(","))];
  return lines.join("\r\n") + "\r\n";
}
var instrumentCreateInputSchema = z16.object({
  marketId: z16.string().min(1),
  symbol: z16.string().max(40),
  displayName: z16.string().max(120).optional(),
  quoteCurrency: z16.string().max(3).optional(),
  tickSize: z16.string().max(40).optional(),
  pointValue: z16.string().max(40).optional(),
  sizeUnit: z16.string().max(20).optional(),
  minSize: z16.string().max(40).optional(),
  sizeStep: z16.string().max(40).optional(),
  tvTicker: z16.string().max(60).optional(),
  priceSymbol: z16.string().max(40).optional()
}).strip();
function withMarketDefaults(input, market) {
  const blank = (v) => v === void 0 || v.trim() === "";
  const symbol = input.symbol.trim().toUpperCase();
  const d = market.defaults;
  return {
    ...input,
    symbol,
    displayName: blank(input.displayName) ? symbol : input.displayName,
    quoteCurrency: blank(input.quoteCurrency) ? market.currency ?? void 0 : input.quoteCurrency,
    tickSize: blank(input.tickSize) ? defaultTickSize(symbol, d) ?? void 0 : input.tickSize,
    pointValue: blank(input.pointValue) ? d.pointValue ?? void 0 : input.pointValue,
    sizeUnit: blank(input.sizeUnit) ? d.sizeUnit ?? void 0 : input.sizeUnit.toUpperCase(),
    minSize: blank(input.minSize) ? d.minSize ?? void 0 : input.minSize,
    sizeStep: blank(input.sizeStep) ? d.sizeStep ?? void 0 : input.sizeStep
  };
}
export {
  ALERTS,
  ALL_SOURCES,
  ANALYTICS_GROUPS,
  ANALYTICS_GROUP_LABEL,
  APPLICATION_STATUSES,
  APPLICATION_STATUS_LABEL,
  CHECKLIST_BAND_LABEL,
  COMMENT_MAX_LENGTH,
  COMPLIANCE_METRICS,
  COMPLIANCE_PERIODS,
  CONTENT_LEVELS,
  CONTEXT_TYPES,
  CSV_HEADER,
  DEFAULT_RISK_POLICY,
  DESTINATION_KINDS,
  DESTINATION_KIND_LABEL,
  DIGEST_HOURS,
  DISCUSSABLE_TARGETS,
  DRAFT_POLICY_TEXTS,
  DUE_OUTCOMES,
  Dec,
  EGRESS_LAYOUT_PATH,
  EGRESS_OVERLAY_POLL_SECONDS,
  EGRESS_START_SLATE_SECONDS,
  ENROLMENT_LIMITS,
  FACEBOOK_RTMPS_URL,
  FEED_TABS,
  FEED_TYPES,
  FILE_ACCEPT,
  FILE_TYPES,
  FOLLOW_TARGETS,
  FSB_GRADES,
  FSB_LABEL,
  FX_PROVIDER,
  GUEST_IDENTITY_PREFIX,
  HANDOVER_LIVE,
  HANDOVER_RIGHTS,
  HANDOVER_RIGHTS_LABEL,
  HANDOVER_STATUS_LABEL,
  INSTRUMENT_CSV_COLUMNS,
  JPY_PAIR_TICK,
  LIVE_LIMITS,
  LIVE_TOKEN_TTL_SECONDS,
  MAX_IMPORT_ROWS,
  MAX_UPLOAD_BYTES,
  MIN_IMPORT_COLUMNS,
  NO_STRATEGY,
  ONBOARDING_TAG,
  OPEN_APPLICATION_STATUSES,
  OPPOSITE_REACTION,
  OTHER_STRATEGIES_LABEL,
  OUTPUT_STATUSES,
  OUTPUT_STATUS_LABEL,
  OUTPUT_STATUS_TONE,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  POLICY_KINDS,
  POLICY_KIND_LABEL,
  PRICE_SOURCES,
  PROGRESS_SCHEDULE_KEYS,
  PUBLISH_ACTIONS,
  PUBLISH_DECISIONS,
  PUBLISH_DECISION_LABEL,
  PUBLISH_MODES,
  PUBLISH_PRIVACIES,
  PUBLISH_PRIVACY_LABEL,
  PUBLISH_STATUSES,
  PUBLISH_STATUS_LABEL,
  PUBLISH_STATUS_TONE,
  REACTION_KINDS,
  RECORDING_STATUSES,
  RECORDING_STATUS_LABEL,
  RECORDING_STATUS_TONE,
  REMINDER_REPEATS,
  REVIEW_VERDICTS,
  ROLES,
  ROLE_LABEL,
  ROOM_PREFIX,
  ROOM_SHARE_MODES,
  ROOM_SHARE_MODE_LABEL,
  ROYAL_LABEL,
  ROYAL_REFS,
  SCHEDULE_MIN_LEAD_MINUTES,
  SCOPE_LABEL,
  SCOPE_PRECEDENCE,
  SEED_MARKETS,
  SETTINGS,
  SETTING_EDITORS,
  SETTING_GROUPS,
  SETUP_ACTIVE_STATUSES,
  SETUP_FREQUENCIES,
  SETUP_LINKABLE_STATUSES,
  SETUP_STATUSES,
  SETUP_STATUS_LABEL,
  SETUP_UPDATE_STATUSES,
  SIGNAL_CONFIDENCES,
  SIGNAL_EVENTS,
  SIGNAL_EVENT_LABEL,
  SIGNAL_FLASH_FALLBACK_MS,
  SIGNAL_MAX_BARS_AGO,
  SIGNAL_MAX_BATCH,
  SIGNAL_PAYLOAD_VERSION,
  SIGNAL_REJECTION_LABEL,
  SIGNAL_REJECTION_REASONS,
  SIGNAL_TABLE_MAX_LIMIT,
  SIGNAL_TABLE_SORTS,
  SIGNAL_WEBHOOK_MAX_BYTES,
  SNOOZE_MINUTES,
  STREAM_PRIVACIES,
  STREAM_PRIVACY_LABEL,
  THUMBNAIL_HEIGHT,
  THUMBNAIL_MAX_BYTES,
  THUMBNAIL_VARIANT_COUNT,
  THUMBNAIL_VARIANT_LABEL,
  THUMBNAIL_WIDTH,
  TRADER_SHORTER_ON_OWN_TRADE,
  WEBINAR_AUDIENCES,
  WEBINAR_AUDIENCE_LABEL,
  WEBINAR_BACKSTAGE_MINUTES,
  WEBINAR_LAYOUTS,
  WEBINAR_LAYOUT_LABEL,
  WEBINAR_PREFIX,
  WEBINAR_STATUSES,
  YOUTUBE_CALLBACK_PATH,
  YOUTUBE_SCOPES,
  YOUTUBE_TAGS_MAX_CHARS,
  accountCreateSchema,
  activationSchema,
  activeMentionQuery,
  addOutcome,
  allowedReactions,
  analysisAccuracy,
  analysisDeadline,
  analysisDeadlineSchema,
  analysisReviewOpensAt,
  analysisReviewSchema,
  analysisSaveSchema,
  analysisSectionSchema,
  analyticsCsv,
  analyticsQuerySchema,
  applicationDecisionSchema,
  applicationFieldsSchema,
  applicationQueueOrder,
  applicationSubmitSchema,
  applicationUpdateSchema,
  approvalDecisionSchema,
  assignAccountSchema,
  assignSymbolsSchema,
  audienceSchema,
  auditQuerySchema,
  barsAgoNow,
  barsElapsed,
  broadcastDescription,
  broadcastLinksSchema,
  broadcastSchema,
  buildWebinarIcs,
  cadenceLabel,
  canEditSetting,
  canMoveSetup,
  channelsFor,
  chatModerateSchema,
  chatSchema,
  checklistAnswersSchema,
  checklistBand,
  checklistItemSchema,
  cleanFilename,
  closeTradeSchema,
  closingReportSchema,
  collectionSchema,
  commentCreateSchema,
  commentEditSchema,
  commentEditableUntil,
  commentHideSchema,
  compareCompliance,
  complianceItemsQuerySchema,
  compliancePeriod,
  complianceQuerySchema,
  complianceRate,
  convert,
  createUserSchema,
  csvCell,
  csvSafeCell,
  dec,
  decimalString,
  defaultSetupExpiry,
  defaultTickSize,
  describeSetting,
  describeSettings,
  discussSchema,
  draftPublishDescription,
  draftPublishTags,
  dueOutcome,
  dueStages,
  effectiveQuerySchema,
  egressLayoutParam,
  egressRtmpUrl,
  emailField,
  emptyCounts,
  emptyHandoverSummary,
  evaluateRisk,
  executeSchema,
  explainSetting,
  feedQuerySchema,
  fileExtension,
  fileTypeByMime,
  fileTypeFor,
  findMentions,
  fmtDuration,
  followSchema,
  formatMoney,
  formatPct,
  formatSettingValue,
  frequencyKey,
  fxRate,
  fxRateSchema,
  grantPermission,
  guestIdentity,
  guestInviteSchema,
  guestRegisterSchema,
  handoverCoversAt,
  handoverCreateSchema,
  handoverSummaryText,
  holidayOn,
  ianaTimezone,
  imageTypeOf,
  inQuietHours,
  instrumentCreateInputSchema,
  instrumentImportTemplate,
  instrumentSchema,
  instrumentUpdateSchema,
  inviteCreateSchema,
  isAnalysisDay,
  isInSession,
  isInlineKind,
  isReviewableSection,
  isSettingKey,
  isSetupActive,
  jsonDiff,
  lastFour,
  learningAssignSchema,
  learningProgressSchema,
  libraryItemCreateSchema,
  libraryItemUpdateSchema,
  liveGrant,
  liveRoomName,
  localDate,
  localInputToUtc,
  loginSchema,
  markToMarket,
  marketCode,
  marketCreateSchema,
  marketHolidaySchema,
  marketHolidaysSchema,
  marketInstrumentDefaultsSchema,
  marketSessionSchema,
  marketUpdateSchema,
  maskSecret,
  matchesMagic,
  maxDrawdown,
  mediaFragment,
  memberStatusSchema,
  mentionParts,
  mentionedUserIds,
  moneyMetrics,
  newPasswordField,
  nextDigestAt,
  nextOccurrence,
  nextOpenAfterClose,
  nextProgressDue,
  nextSessionClose,
  nextSessionOpen,
  nextSetupUpdateDue,
  offsetLabel,
  outputStatusAfter,
  overlayFor,
  parseAnalysisTarget,
  parseClock,
  parseCsv,
  parseEgressLayout,
  parseIdentity,
  parseInstrumentImport,
  parseLiveRoomName,
  parseSymbolList,
  parseTargetKey,
  parseWebhookBody,
  passwordChangeSchema,
  passwordForgotSchema,
  passwordResetSchema,
  pendingPolicies,
  performanceMetrics,
  periodFields,
  periodRules,
  policyAcceptSchema,
  policyPublishSchema,
  positionSize,
  positiveDecimal,
  priceAge,
  priceLabel,
  priceProxyNote,
  priceSourceName,
  priceSymbolSchema,
  privacyForcedPrivate,
  privacyFromDefault,
  profitFactor,
  progressReportSchema,
  publishApproveSchema,
  publishBlocker,
  publishChangesSchema,
  publishDescription,
  publishPackageSchema,
  publishRejectSchema,
  publishRetrySchema,
  publishStatusAfter,
  publishSubmitSchema,
  questionModerateSchema,
  questionSchema,
  quietHoursEnd,
  quietHoursOf,
  quietHoursStart,
  rMultiple,
  reactionToggleSchema,
  readBarsAgo,
  readConfidence,
  readFsbGrade,
  readHandoverSummary,
  readReceiptSchema,
  readRoyalRef,
  recordingKey,
  recordingLibrarySchema,
  recordingStatusAfter,
  recordingTrimSchema,
  redactRtmp,
  reminderInstant,
  reminderOffsetsSchema,
  reminderSchema,
  reminderUpdateSchema,
  reportingCurrencySchema,
  resolveSetting,
  riskPolicyUpdateSchema,
  roleChangeSchema,
  roomCreateSchema,
  roomInviteSchema,
  roomModeSchema,
  roomModerationSchema,
  rtmpDestinationSchema,
  scheduleProblem,
  scoreChecklist,
  sectionTargetId,
  sessionsOn,
  settingChangeSchema,
  settingClearSchema,
  settingEditors,
  settingWriteSchema,
  setupCreateSchema,
  setupEditSchema,
  setupLevelsIssue,
  setupPlanDraft,
  setupPostUpdateSchema,
  shareDecisionSchema,
  signalEventCode,
  signalExtras,
  signalFlashUntil,
  signalIssues,
  signalPayloadSchema,
  signalSetupDraft,
  signalSourceCreateSchema,
  signalTableQuerySchema,
  slateSchema,
  snoozeSchema,
  splitTicker,
  stageSchema,
  statusLinkRequestSchema,
  strategyContentSchema,
  strategyCreateSchema,
  strategyExampleSchema,
  strategyStats,
  streamRetryDelaySeconds,
  suggestMembers,
  symbolCode,
  symbolRequestDecisionSchema,
  symbolRequestSchema,
  takeProfitSchema,
  targetKey,
  targetRefSchema,
  thumbnailKey,
  thumbnailSvg,
  timeframeMs,
  totalHandoverSummary,
  totpCodeSchema,
  totpEnrolSchema,
  tradeEventSchema,
  tradePlanSchema,
  tradingDayOf,
  trimProblem,
  tvTickerSchema,
  updateMeSchema,
  utcToLocalInput,
  validateSettingValue,
  videoEmbed,
  vtfTimeframe,
  watchlistItems,
  webinarInputSchema,
  webinarIsOpenForAudience,
  webinarLayoutSchema,
  webinarReminderTimes,
  webinarRoomName,
  webinarUpdateSchema,
  withMarketDefaults,
  wrapLines,
  youtubeStatus
};
